import * as TOKENS from '@dashhudson/dashing-ui/tokens';
import Formatters from '@dashhudson/dashing-ui/utils/formatters';

import { APPROVE_ORDERS_MAX, GIFT_ORDER_STATUS } from '@/apis/creator-gifting';
import enumTypes from '@/app/library/constants';
import newEmptyCampaignAddAdCampaigns1x from '@/assets/images/campaigns-new/empty-campaign-add-ad-campaigns/1x.png';
import newEmptyCampaignAddAdCampaigns2x from '@/assets/images/campaigns-new/empty-campaign-add-ad-campaigns/2x.png';
import newEmptyCampaignAddAdCampaigns3x from '@/assets/images/campaigns-new/empty-campaign-add-ad-campaigns/3x.png';
import newEmptyCampaignAddBoards1x from '@/assets/images/campaigns-new/empty-campaign-add-boards/1x.png';
import newEmptyCampaignAddBoards2x from '@/assets/images/campaigns-new/empty-campaign-add-boards/2x.png';
import newEmptyCampaignAddBoards3x from '@/assets/images/campaigns-new/empty-campaign-add-boards/3x.png';
import newEmptyCampaignAddCreators1x from '@/assets/images/campaigns-new/empty-campaign-add-creators/1x.png';
import newEmptyCampaignAddCreators2x from '@/assets/images/campaigns-new/empty-campaign-add-creators/2x.png';
import newEmptyCampaignAddCreators3x from '@/assets/images/campaigns-new/empty-campaign-add-creators/3x.png';
import newEmptyCampaignAddMedia1x from '@/assets/images/campaigns-new/empty-campaign-add-media/1x.png';
import newEmptyCampaignAddMedia2x from '@/assets/images/campaigns-new/empty-campaign-add-media/2x.png';
import newEmptyCampaignAddMedia3x from '@/assets/images/campaigns-new/empty-campaign-add-media/3x.png';
import emptyCampaignAddAdCampaigns1x from '@/assets/images/campaigns/empty-campaign-add-ad-campaigns/1x.png';
import emptyCampaignAddAdCampaigns2x from '@/assets/images/campaigns/empty-campaign-add-ad-campaigns/2x.png';
import emptyCampaignAddAdCampaigns3x from '@/assets/images/campaigns/empty-campaign-add-ad-campaigns/3x.png';
import emptyCampaignAddBoards1x from '@/assets/images/campaigns/empty-campaign-add-boards/1x.png';
import emptyCampaignAddBoards2x from '@/assets/images/campaigns/empty-campaign-add-boards/2x.png';
import emptyCampaignAddBoards3x from '@/assets/images/campaigns/empty-campaign-add-boards/3x.png';
import emptyCampaignAddCreators1x from '@/assets/images/campaigns/empty-campaign-add-creators/1x.png';
import emptyCampaignAddCreators2x from '@/assets/images/campaigns/empty-campaign-add-creators/2x.png';
import emptyCampaignAddCreators3x from '@/assets/images/campaigns/empty-campaign-add-creators/3x.png';
import emptyCampaignAddMedia1x from '@/assets/images/campaigns/empty-campaign-add-media/1x.png';
import emptyCampaignAddMedia2x from '@/assets/images/campaigns/empty-campaign-add-media/2x.png';
import emptyCampaignAddMedia3x from '@/assets/images/campaigns/empty-campaign-add-media/3x.png';
import {
  CAMPAIGN_PAGE,
  DUPLICATE_CAMPAIGN_CONTENT,
  METRIC_SOURCE,
} from '@/models/campaigns/constants';
import {
  CHANNELS as DASHBOARDS_CHANNELS,
  FRONTEND_TO_BACKEND_CHANNEL_MAP,
} from '@/models/dashboards/channels.enum';
import { DATA_TYPES as METRIC_DATA_TYPES } from '@/models/dashboards/metrics.constants';
import { isBeforeMetricsUpgrade as isBeforeFbMetricsUpgrade } from '@/models/facebook/metrics-upgrade';
import { PLATFORM_CONNECTION } from '@/models/platform/platform-connection.enum';
import { lastDateRange } from '@/utils/dateUtils';
import { requireFeatureFlag } from '@/utils/predicate-utils';
import { srcSet } from '@/utils/srcset';
import { colours } from '@/ux/colours';

export { CAMPAIGN_PAGE, DUPLICATE_CAMPAIGN_CONTENT, METRIC_SOURCE };

export const PDF_REPORT_WIDTH = 1440;

/** @type {import('@/utils/pdfPage').PdfPageLayout} */
export const CAMPAIGN_PDF_PAGE = {
  width: PDF_REPORT_WIDTH,
  height: 1920,
  margin: { x: 24, y: 40 },
};

export const CHANNELS = Object.freeze({
  TIKTOK: 'TIKTOK',
  INSTAGRAM: 'INSTAGRAM',
  INSTAGRAM_STORY: 'INSTAGRAM_STORY',
  FACEBOOK: 'FACEBOOK',
  PINTEREST: 'PINTEREST',
  TWITTER: 'TWITTER',
  YOUTUBE: 'YOUTUBE',
  RELATIONSHIPS: 'RELATIONSHIPS',
  UGC: 'UGC',
  TIKTOK_UGC: 'TIKTOK_UGC',
});

export const DATA_SOURCES = Object.freeze({
  TIKTOK_LIKESHOP: 'TIKTOK_LIKESHOP',
  LIKESHOP: 'LIKESHOP',
  INSTAGRAM_CREATORS: 'INSTAGRAM_CREATORS',
  TIKTOK_CREATORS: 'TIKTOK_CREATORS',
  ...CHANNELS,
});

export const CAMPAIGN_UGC_SOURCES = Object.freeze({
  [CHANNELS.UGC]: [CHANNELS.INSTAGRAM, CHANNELS.INSTAGRAM_STORY],
  [CHANNELS.TIKTOK_UGC]: [CHANNELS.TIKTOK],
});

export const OWNED_SUMMARY_DASHBOARDS_SOURCES = Object.freeze([
  DASHBOARDS_CHANNELS.INSTAGRAM.value,
  DASHBOARDS_CHANNELS.INSTAGRAM_UGC.value,
  DASHBOARDS_CHANNELS.INSTAGRAM_STORIES.value,
  DASHBOARDS_CHANNELS.TIKTOK.value,
  DASHBOARDS_CHANNELS.TIKTOK_UGC.value,
  DASHBOARDS_CHANNELS.FACEBOOK.value,
  DASHBOARDS_CHANNELS.PINTEREST.value,
  DASHBOARDS_CHANNELS.YOUTUBE.value,
  DASHBOARDS_CHANNELS.TWITTER.value,
]);

// Story UGC has no campaign data source of its own — a campaign records it under `has_ugc`, same
// as feed UGC — so this stands in as one, keying the icon and platform label maps further down.
const INSTAGRAM_STORY_UGC_SOURCE = 'INSTAGRAM_STORY_UGC';

export const DASHBOARDS_CHANNEL_TO_CAMPAIGN_SOURCE = Object.freeze({
  [DASHBOARDS_CHANNELS.INSTAGRAM_UGC.value]: DATA_SOURCES.UGC,
  [DASHBOARDS_CHANNELS.INSTAGRAM_STORIES.value]: DATA_SOURCES.INSTAGRAM_STORY,
  [DASHBOARDS_CHANNELS.INSTAGRAM_STORIES_UGC.value]: INSTAGRAM_STORY_UGC_SOURCE,
  [DASHBOARDS_CHANNELS.TIKTOK_UGC.value]: DATA_SOURCES.TIKTOK_UGC,
});

// Pages that report more than one channel without a tab for each. Mirrors PAGE_COMBINED_CHANNELS
// in library-backend; the page's own channel comes first, as the metrics it names are saved.
export const CAMPAIGN_PAGE_COMBINED_CHANNELS = Object.freeze({
  [CAMPAIGN_PAGE.INSTAGRAM_UGC]: [
    DASHBOARDS_CHANNELS.INSTAGRAM_UGC.value,
    DASHBOARDS_CHANNELS.INSTAGRAM_STORIES_UGC.value,
  ],
});

// A combined page groups its picker by how many of its channels a metric covers. No story-only
// section: every story metric is `connected_accounts_only`, which the picker drops.
export const COMBINED_CHANNEL_METRIC_SECTIONS = Object.freeze({
  SHARED: 'COMBINED_SHARED',
  [DASHBOARDS_CHANNELS.INSTAGRAM_UGC.value]: 'COMBINED_INSTAGRAM_UGC',
});

export const COMBINED_CHANNEL_METRIC_SECTION_ORDER = Object.freeze([
  COMBINED_CHANNEL_METRIC_SECTIONS.SHARED,
  COMBINED_CHANNEL_METRIC_SECTIONS[DASHBOARDS_CHANNELS.INSTAGRAM_UGC.value],
]);

export const COMBINED_CHANNEL_METRIC_SECTION_TITLES = Object.freeze({
  [COMBINED_CHANNEL_METRIC_SECTIONS.SHARED]: 'Instagram UGC & Stories',
  [COMBINED_CHANNEL_METRIC_SECTIONS[DASHBOARDS_CHANNELS.INSTAGRAM_UGC.value]]: 'Instagram UGC Only',
});

export const AD_SOURCES = Object.freeze({
  META_ADS: 'META_ADS',
  META_ADS_CAMPAIGNS_DETAIL: 'META_ADS_CAMPAIGN_DETAILS',
  TIKTOK_ADS: 'TIKTOK_ADS',
});

export const AD_SOURCE_NAMES = Object.freeze({
  [AD_SOURCES.META_ADS]: 'Meta',
  [AD_SOURCES.TIKTOK_ADS]: 'TikTok',
});

export const CHANNELS_FOR_POSTS_AND_BOARDS = Object.freeze([
  'instagram',
  'instagram-story',
  'tiktok',
  'facebook',
  'pinterest',
  'youtube',
  'x',
]);

export const CHANNELS_FOR_CREATORS = Object.freeze(['instagram', 'tiktok', 'youtube']);

export const CHANNELS_FOR_AD_CAMPAIGNS = Object.freeze(['tiktok', 'meta']);

export const CAMPAIGN_BOARD_GALLERY_TYPES =
  'TIKTOK,INSTAGRAM,INSTAGRAM_STORY,FACEBOOK,PINTEREST,TWITTER,YOUTUBE';

const CAMPAIGN_LIST_SORT_OPTIONS_LEGACY = [
  {
    label: 'Recently Added',
    value: 'CREATED',
  },
  {
    label: 'Campaign Name',
    value: 'NAME',
  },
];

export const CAMPAIGN_LIST_SORT_OPTIONS = [
  {
    label: 'Date Added',
    value: 'CREATED',
  },
  {
    label: 'Name',
    value: 'NAME',
  },
];

// For users with access to premium analytics (specifically can_access_campaigns)
export const PREMIUM_ANALYTICS_ADDITIONAL_CAMPAIGN_SORT_OPTIONS = [
  {
    label: 'Avg. Eng. Rate',
    value: 'AVG_ENGAGEMENT_RATE',
  },
  {
    label: 'Video Views',
    value: 'TOTAL_VIDEO_VIEWS',
  },
  {
    label: 'Engagements',
    value: 'TOTAL_ENGAGEMENTS',
  },
  {
    label: 'Impressions',
    value: 'TOTAL_IMPRESSIONS',
  },
];

export const CAMPAIGN_LIST_DEFAULT_SORT_LEGACY = {
  sortOrder: 'ASC',
  selected: CAMPAIGN_LIST_SORT_OPTIONS_LEGACY[0],
};

export const CAMPAIGN_LIST_DEFAULT_SORT = {
  sortOrder: 'ASC',
  selected: CAMPAIGN_LIST_SORT_OPTIONS[0],
};

export const tooltips = {
  topStatNumberOfPosts: 'The number of posts published across all channels',
  topStatVideoViews: 'The number of video views on posts published across all channels',
  topStatVideoViewsV2: 'The total number of times your video posts were played',
  topStatTotalEngagements: 'The total engagements on posts published across all channels',
  topStatAvgEngagementRate:
    'The average engagement rate for your posts published across all channels',
  topStatImpressions: 'The number of impressions for your posts published across all channels',
  topStatImpressionsV2: 'The number of times your posts were viewed or displayed',
  topStatClicks: 'The number of clicks on posts published across all channels',
  noMediaOnBoard: 'There is no media in this board.',
  BarLineChartAvgEngagement: 'Your average engagement rate over the last 12 weeks from today.',
  engagementRateChart: {
    campaign: 'The average engagement rate for your posts on this channel within this Campaign',
    campaignAverage: 'The average engagement rate across all channels within this Campaign',
    channelAverage: 'The average engagement rate for this channel',
  },
  sentiment: {
    overall: 'The sentiment breakdown of comments on owned posts in this Campaign',
  },
  impressionsChart: 'The number of times your posts were viewed or displayed',
};

export const accessMessages = {
  noUserAccessTitle: "Looks like you don't have permission to use this feature!",
  noAccessMessage:
    'Dash Social brings you all of the insights you love, including Campaigns! Ask your Dash Social administrator to provide you access.',
};

export const chartColors = {
  blue: colours.CHART.SET.CHART_SET_01,
  green: colours.CHART.SET.CHART_SET_02,
  orange: colours.CHART.SET.CHART_SET_03,
};

export const channelList = {
  TIKTOK: 'TikTok',
  INSTAGRAM: 'Instagram',
  FACEBOOK: 'Facebook',
  PINTEREST: 'Pinterest',
  TWITTER: 'X',
  YOUTUBE: 'YouTube',
  INSTAGRAM_STORY: ['Instagram', 'Stories'],
};
export const CAMPAIGN_TOP_LINE_STATS = {
  INSTAGRAM: [
    {
      title: 'Views',
      valueKey: 'views',
      metric: 'VIEWS',
      organicKey: 'views',
      tooltip:
        "The number of times your posts in this Campaign were displayed on a person's screen. This includes repeat views.",
      format: 'number',
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Avg. Watch Time',
      valueKey: 'avg_avg_watch_time_sec',
      metric: 'AVG_AVG_WATCH_TIME',
      organicKey: 'avg_avg_watch_time_sec',
      tooltip:
        'The average amount of time users spent playing your Reels in this Campaign in a single viewing',
      format: 'duration',
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Engagements',
      valueKey: 'engagements',
      metric: 'TOTAL_ENGAGEMENTS',
      organicKey: 'engagements',
      tooltip: {
        content: 'The sum of engagements received on your posts in this Campaign',
        footer:
          'Engagements = (Likes + Saves + Comments + Shares + Reposts) - (Unlikes + Unsaves + Deleted comments)',
      },
      format: 'number',
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Avg. Eng. Rate (Followers)',
      valueKey: 'avg_engagement_rate',
      metric: 'AVG_ENGAGEMENT_RATE',
      organicKey: 'avg_engagement_rate',
      format: 'percent',
      formatOptions: {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
      tooltip: {
        footer: 'Instagram Avg. Engagement Rate = (Engagements / Followers) / Number of Posts',
      },
    },
    {
      title: 'Avg. Eng. Rate (Views)',
      valueKey: 'avg_engagement_rate_views',
      metric: 'AVG_ENGAGEMENT_RATE_VIEWS',
      organicKey: 'avg_engagement_rate_views',
      format: 'percent',
      formatOptions: {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
      tooltip: {
        footer: 'Instagram Avg. Engagement Rate = (Engagements / Views) / Number of Posts',
      },
    },
    {
      title: 'Avg. Effectiveness Rate',
      valueKey: 'avg_effectiveness_engagements_rate',
      metric: 'AVG_EFFECTIVENESS_ENGAGEMENTS',
      organicKey: 'avg_effectiveness_engagements_rate',
      format: 'percent',
      formatOptions: {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
      tooltip: {
        content:
          'The average percentage of users that saw your posts in this Campaign and engaged with them',
        footer: 'Avg. Effectiveness Rate = Engagements / Reach',
      },
    },
    {
      title: 'Avg. Reach',
      format: 'number',
      formatOptions: {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      },
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
      tooltip: 'The average number of unique accounts who saw your posts in this Campaign',
      valueKey: 'avg_reach',
      metric: 'AVG_REACH',
      organicKey: 'avg_reach',
    },
  ],
  TIKTOK: [
    {
      title: 'Video Views',
      valueKey: 'video_views',
      metric: 'TOTAL_VIDEO_VIEWS',
      tooltip: 'The number of times your posts in this Campaign were viewed',
      format: 'number',
      postType: 'ORGANIC_PROMOTED',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Engagements',
      valueKey: 'engagements',
      metric: 'TOTAL_ENGAGEMENTS',
      tooltip: {
        content: 'The sum of engagements for your posts in this Campaign',
        footer: 'Engagements = Likes + Comments + Shares',
      },
      format: 'number',
      postType: 'ORGANIC_PROMOTED',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Avg. Eng. Rate (Reach)',
      valueKey: 'avg_engagement_rate',
      metric: 'AVG_ENGAGEMENT_RATE',
      tooltip: {
        footer: 'Avg. Engagement Rate = (Engagements / Reach) / Number of Posts',
      },
      postType: 'ORGANIC_PROMOTED',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
      format: 'percent',
      formatOptions: {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    },
    {
      title: 'Avg. Eng. Rate (Video Views)',
      valueKey: 'avg_engagement_rate_views',
      metric: 'VIEWS_BASED_ENGAGEMENT_RATE',
      tooltip: {
        footer: 'Avg. Engagement Rate = (Engagements / Video Views) / Number of Posts',
      },
      postType: 'ORGANIC_PROMOTED',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
      format: 'percent',
      formatOptions: {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    },
    {
      title: 'Avg. Retention Rate',
      valueKey: 'avg_retention_rate',
      metric: 'AVG_RETENTION_RATE',
      tooltip: {
        content:
          'The average percentage of your videos in this Campaign that your audience watched',
        footer: 'Retention Rate = Average Time Watched / Video Duration',
      },
      format: 'percent',
      formatOptions: {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
      postType: 'ORGANIC_PROMOTED',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Avg. Reach',
      valueKey: 'avg_reach',
      metric: 'AVG_REACH',
      tooltip: 'The average number of unique accounts who saw your posts in this Campaign',
      format: 'number',
      formatOptions: {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      },
      postType: 'ORGANIC_PROMOTED',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Avg. FYP Views %',
      valueKey: 'fyp_views_percentage',
      metric: 'AVG_FYP_VIEWS',
      tooltip:
        'The average percentage of your videos in this Campaign that your audience watched on their For You Page',
      format: 'percent',
      formatOptions: {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
      postType: 'ORGANIC_PROMOTED',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
  ],
  INSTAGRAM_STORY: [
    {
      title: 'Avg. Reach',
      valueKey: 'avg_reach',
      metric: 'AVG_REACH',
      tooltip: 'The average number of unique accounts who saw your story frames in this Campaign',
      format: 'number',
      formatOptions: {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      },
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Views',
      valueKey: 'views',
      metric: 'VIEWS',
      tooltip:
        'The number of times your story frames in this Campaign were displayed.\n\nThis metric is only available for reporting after March 24, 2025.',
      format: 'number',
      formatOptions: {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      },
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Avg. Completion Rate',
      valueKey: 'avg_completion_rate_views',
      metric: 'AVG_COMPLETION_RATE_VIEWS',
      tooltip: {
        content:
          'The average completion rate of the story frames in this Campaign.\n\nThis metric is only available for reporting after March 24, 2025.',
        footer: 'Completion Rate = (Views - Exits) / Views',
      },
      format: 'percent',
      formatOptions: {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Avg. Exit Rate',
      valueKey: 'avg_exit_rate_views',
      metric: 'AVG_EXIT_RATE_VIEWS',
      tooltip: {
        content:
          'The average exit rate of the story frames in this Campaign.\n\nThis metric is only available for reporting after March 24, 2025.',
        footer: 'Exit Rate = Exits / Views',
      },
      format: 'percent',
      formatOptions: {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Exits',
      valueKey: 'exits',
      metric: 'EXITS',
      tooltip:
        'The amount of users who exited the stories view without clicking forward or watching the entire story. An exit is counted even if it occurs at the last millisecond.',
      format: 'number',
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Link Clicks',
      valueKey: 'link_clicks',
      metric: 'SWIPE_UPS',
      tooltip:
        'The number of link clicks on story frames in this Campaign.\n\nOnly links scheduled through the Dash scheduler are trackable here.',
      format: 'number',
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
  ],
  FACEBOOK: [
    {
      title: 'Views (Video Media)',
      valueKey: 'views_only_videos',
      metric: 'PAID_AND_ORGANIC_VIEWS_BY_POST_VIDEO_REEL',
      tooltip: 'The number of times your videos were played for at least 1ms.',
      format: 'number',
      postType: 'ORGANIC_PROMOTED',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Views',
      valueKey: 'views',
      metric: 'PAID_AND_ORGANIC_VIEWS_BY_POST',
      tooltip:
        'The number of times your videos were played for at least 1ms plus the number of times your photos and text posts were on screen.',
      format: 'number',
      postType: 'ORGANIC_PROMOTED',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Engagements',
      valueKey: 'engagements_v2',
      metric: 'TOTAL_ENGAGEMENTS_POSTS_V2',
      tooltip: {
        content: 'The sum of engagements on your posts',
        footer:
          'Engagements = Reactions + Shares + Comments\n\nReactions include Likes, Love, Haha, Wow, Sad, Angry and Care',
      },
      format: 'number',
      postType: 'ORGANIC_PROMOTED',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Avg. Engagement Rate',
      valueKey: 'avg_engagement_rate_v2',
      metric: 'AVG_ENGAGEMENT_RATE_V2',
      tooltip: {
        content: 'The average engagement rate for your posts',
        footer: 'Engagement Rate = (Engagements / Views) / Number of Posts',
      },
      format: 'percent',
      formatOptions: {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
      postType: 'ORGANIC_PROMOTED',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Avg. Effectiveness',
      valueKey: 'avg_effectiveness_rate_v2',
      metric: 'AVG_EFFECTIVENESS_V2',
      tooltip: {
        content: 'The percentage of users that saw your post and engaged with it',
        footer: 'Effectiveness = (Engagements / Viewers) / Number of Posts',
      },
      format: 'percent',
      formatOptions: {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
      postType: 'ORGANIC_PROMOTED',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Avg. Viewers',
      valueKey: 'avg_reach_v2',
      metric: 'AVG_PAID_AND_ORGANIC_POST_REACH_V2',
      tooltip: ({ startDate } = {}) => {
        const result = {
          content:
            'The average number of unique accounts that have viewed your posts in this Campaign at least once.',
          footer: 'Avg. Viewers = Viewers / Number of Posts',
        };
        if (isBeforeFbMetricsUpgrade(startDate)) {
          result.footer +=
            '\n\nNote: This campaign includes a mix of Viewers and legacy Reach values from posts published before July 31st 2025.';
        }
        return result;
      },
      format: 'number',
      formatOptions: {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      },
      postType: 'ORGANIC_PROMOTED',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
  ],
  PINTEREST: [
    {
      title: 'Video Views',
      valueKey: 'video_views',
      metric: 'TOTAL_VIDEO_VIEWS',
      tooltip: 'The number of views for at least two seconds with 50% of the video Pin in view',
      format: 'number',
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Impressions',
      valueKey: 'impressions',
      metric: 'TOTAL_IMPRESSIONS',
      tooltip: 'The number of times your Pins in this Campaign were displayed ',
      format: 'number',
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Engagements',
      valueKey: 'engagements',
      metric: 'ENGAGEMENTS',
      tooltip: {
        content: 'The sum of engagements your Pins in this Campaign received',
        footer: 'Engagements = Pin Clicks + Outbound Clicks + Saves',
      },
      format: 'number',
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Avg. Engagement Rate',
      valueKey: 'avg_engagement_rate',
      metric: 'TOTAL_AVG_ENGAGEMENT_RATE',
      tooltip: {
        content: 'The average engagement rate for your Pins in this Campaign',
        footer: 'Engagement Rate = (Pin Clicks + Outbound Clicks + Saves) / Impressions',
      },
      format: 'percent',
      formatOptions: {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Saves',
      valueKey: 'saves',
      metric: 'TOTAL_SAVES',
      tooltip: 'The number of times your Pins in this Campaign were saved',
      format: 'number',
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Outbound Clicks',
      valueKey: 'outbound_clicks',
      metric: 'TOTAL_LINK_CLICKS',
      tooltip:
        'The total number of times people clicked and followed the links attached to your Pins to the destination URL',
      format: 'number',
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
  ],
  TWITTER: [
    {
      title: 'Video Views',
      valueKey: 'video_views',
      metric: 'TOTAL_VIDEO_VIEWS',
      tooltip: 'The number of times your videos in this Campaign were viewed',
      format: 'number',
      postType: 'ORGANIC_PROMOTED',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Impressions',
      valueKey: 'impressions',
      metric: 'IMPRESSIONS_BY_POST',
      tooltip: 'The number of times your posts in this Campaign were displayed',
      format: 'number',
      postType: 'ORGANIC_PROMOTED',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Engagements',
      valueKey: 'engagements',
      metric: 'TOTAL_ENGAGEMENTS_POSTS',
      tooltip: {
        content: 'The sum of engagements your posts in this Campaign received',
        footer: 'Engagements = Reposts + Replies + Likes + Link Clicks',
      },
      format: 'number',
      postType: 'ORGANIC_PROMOTED',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Avg. Engagement Rate',
      valueKey: 'avg_engagement_rate',
      metric: 'TOTAL_AVG_ENGAGEMENT_RATE',
      tooltip: {
        content: 'The average engagement rate for your posts in this Campaign',
        footer: 'Engagement Rate = Engagements / Impressions',
      },
      format: 'percent',
      formatOptions: {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
      postType: 'ORGANIC_PROMOTED',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Likes',
      valueKey: 'likes',
      metric: 'TOTAL_LIKES',
      tooltip: 'The number of times your posts in this Campaign were liked',
      format: 'number',
      postType: 'ORGANIC_PROMOTED',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Link Clicks',
      valueKey: 'link_clicks',
      metric: 'CLICKS_BY_POST',
      tooltip: 'The number of times people clicked your links in this Campaign',
      format: 'number',
      postType: 'ORGANIC_PROMOTED',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
  ],
  UGC: [
    {
      title: 'Number of Posts',
      valueKey: 'number_of_media',
      metric: 'UGC_NUMBER_OF_POSTS',
      tooltip: 'The number of posts that mentioned or tagged you',
      format: 'number',
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Mentions',
      valueKey: 'mentions',
      metric: 'UGC_MENTIONS',
      tooltip: `The number of times you were @mentioned by other accounts in post captions`,
      format: 'number',
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'People Tags',
      valueKey: 'people_tags',
      metric: 'UGC_PHOTO_TAGS',
      tooltip: `The number of times you were tagged in posts by other accounts`,
      format: 'number',
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Avg. Estimated Reach',
      valueKey: 'estimated_reach',
      metric: 'UGC_AVG_ESTIMATED_REACH',
      tooltip: {
        content:
          'The average number of unique accounts who saw UGC posts that @mentioned or tagged you',
        footer: 'Avg. Estimated Reach = Estimated Reach / Number of Posts',
      },
      format: 'number',
      formatOptions: {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      },
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Engagements',
      valueKey: 'engagements',
      metric: 'UGC_ENGAGEMENTS_PUBLIC',
      tooltip: {
        content: 'The sum of engagements UGC posts that @mentioned or tagged you received',
        footer: 'Engagements = Likes + Comments',
      },
      format: 'number',
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
  ],
  YOUTUBE: [
    {
      title: 'Video Views',
      valueKey: 'video_views',
      metric: 'TOTAL_VIDEO_VIEWS',
      tooltip: 'The number of times your videos in this Campaign were viewed',
      format: 'number',
      postType: 'ORGANIC_PROMOTED',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Engagements',
      valueKey: 'engagements',
      metric: 'TOTAL_ENGAGEMENTS',
      tooltip: 'The total number of engagements your videos in this Campaign received',
      format: 'number',
      postType: 'ORGANIC_PROMOTED',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
      // YouTube compliance: engagements is not an approved metric; hide unless flagged on.
      predicate: requireFeatureFlag('youtubeCompliance'),
    },
    {
      title: 'Watch Time',
      valueKey: 'watch_time',
      metric: 'TOTAL_ESTIMATED_SECONDS_WATCHED',
      tooltip: 'The total amount of time that users watched your videos in this Campaign',
      format: 'duration',
      postType: 'ORGANIC_PROMOTED',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Avg. View Duration',
      valueKey: 'avg_view_duration',
      metric: 'TOTAL_AVG_VIEW_DURATION',
      tooltip: 'The average amount of time that viewers watched per video view ',
      format: 'duration',
      postType: 'ORGANIC_PROMOTED',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Likes',
      valueKey: 'likes',
      metric: 'TOTAL_LIKES',
      tooltip: 'The number of times your videos in this Campaign were liked',
      format: 'number',
      postType: 'ORGANIC_PROMOTED',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Dislikes',
      valueKey: 'dislikes',
      metric: 'TOTAL_DISLIKES',
      tooltip: 'The number of dislikes on your videos in this Campaign',
      format: 'number',
      postType: 'ORGANIC_PROMOTED',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
  ],
  RELATIONSHIPS: [
    {
      title: 'Number of Posts',
      valueKey: 'number_of_media',
      tooltip: 'The number of posts that mentioned or tagged you',
      format: 'number',
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Views',
      valueKey: 'video_views',
      tooltip: 'The number of views on your posts in this Campaign for connected accounts',
      format: 'number',
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Avg. Engagement Rate',
      valueKey: 'avg_engagement_rate',
      tooltip: {
        content: "The average percentage of the creators' audiences that engaged",
        footer: 'Engagement Rate = (Likes + Comments) / Followers',
      },
      format: 'percent',
      formatOptions: {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Engagements',
      valueKey: 'engagements',
      tooltip: {
        content: 'The total number of engagements on posts in this Campaign',
        footer: 'Engagements = Likes + Comments + (Saves + Shares + Reposts if connected)',
      },
      format: 'number',
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Likes',
      valueKey: 'likes',
      tooltip: 'The number of likes on posts in this Campaign',
      format: 'number',
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
    {
      title: 'Comments',
      valueKey: 'comments',
      tooltip: 'The number of comments on posts in this Campaign',
      format: 'number',
      postType: 'ORGANIC',
      metricTimeFrame: 'POSTS_THIS_PERIOD',
    },
  ],
};

export const CAMPAIGN_ADS_TOP_LINE_STATS = Object.freeze({
  [AD_SOURCES.META_ADS]: [
    { metric: 'TOTAL_SPENT' },
    { metric: 'IMPRESSIONS' },
    { metric: 'LINK_CLICKS' },
    { metric: 'VIDEO_VIEWS' },
    { metric: 'CLICK_THROUGH_RATE' },
    { metric: 'COST_PER_MILE' },
    { metric: 'COST_PER_CLICK' },
    { metric: 'ENGAGEMENT_RATE' },
  ],
  [AD_SOURCES.TIKTOK_ADS]: [
    { metric: 'TOTAL_SPENT' },
    { metric: 'IMPRESSIONS' },
    { metric: 'ENGAGEMENTS' },
    { metric: 'CLICK_THROUGH_RATE_DESTINATION' },
    { metric: 'ENGAGED_VIEW' },
    { metric: 'COST_PER_MILE' },
    { metric: 'COST_PER_CLICK' },
    { metric: 'REAL_TIME_CONVERSION_RATE' },
  ],
  MULTI_CHANNEL: [
    { metric: 'TOTAL_SPENT' },
    { metric: 'AVG_COST_PER_CLICK' },
    { metric: 'AVG_COST_PER_MILE' },
    { metric: 'CLICKS_ALL_ADS' },
    { metric: 'IMPRESSIONS_ADS' },
    { metric: 'CLICKS_ADS' },
  ],
});

export const CAMPAIGN_OVERVIEW_DONUT_METRICS = Object.freeze({
  engagements: 'TOTAL_ENGAGEMENTS_V2',
  impressions: 'IMPRESSIONS_V3',
});

const DONUT_BASE_CHANNELS = [
  CHANNELS.INSTAGRAM,
  CHANNELS.FACEBOOK,
  CHANNELS.PINTEREST,
  CHANNELS.TWITTER,
  CHANNELS.TIKTOK,
  CHANNELS.YOUTUBE,
];

// dashboard-backend rejects incompatible channel/metric pairs; Instagram Stories (plural key) is impressions-only.
export const CAMPAIGN_OVERVIEW_DONUT_METRIC_CHANNELS = Object.freeze({
  [CAMPAIGN_OVERVIEW_DONUT_METRICS.engagements]: Object.freeze(DONUT_BASE_CHANNELS),
  [CAMPAIGN_OVERVIEW_DONUT_METRICS.impressions]: Object.freeze([
    ...DONUT_BASE_CHANNELS,
    'INSTAGRAM_STORIES',
  ]),
});

export const CAMPAIGN_CHANNEL_TO_TOPLINE_PAGE = Object.freeze({
  [CHANNELS.INSTAGRAM]: 'INSTAGRAM',
  [CHANNELS.INSTAGRAM_STORY]: 'INSTAGRAM_STORIES',
  [CHANNELS.TIKTOK]: 'TIKTOK',
  [CHANNELS.FACEBOOK]: 'FACEBOOK',
  [CHANNELS.PINTEREST]: 'PINTEREST',
  [CHANNELS.TWITTER]: 'X',
  [CHANNELS.YOUTUBE]: 'YOUTUBE',
  [CHANNELS.UGC]: 'INSTAGRAM_UGC',
});

export const CAMPAIGN_SOURCE_TO_DASHING_UI_CHANNEL = Object.freeze({
  [CHANNELS.INSTAGRAM]: 'instagram',
  [CHANNELS.INSTAGRAM_STORY]: 'instagram_story',
  [CHANNELS.TIKTOK]: 'tiktok',
  [CHANNELS.FACEBOOK]: 'facebook',
  [CHANNELS.PINTEREST]: 'pinterest',
  [CHANNELS.TWITTER]: 'x',
  [CHANNELS.YOUTUBE]: 'youtube',
  [AD_SOURCES.META_ADS]: 'meta',
  [AD_SOURCES.TIKTOK_ADS]: 'tiktok',
  [CHANNELS.UGC]: 'instagram',
  [INSTAGRAM_STORY_UGC_SOURCE]: 'instagram_story',
  [CHANNELS.TIKTOK_UGC]: 'tiktok',
  [DATA_SOURCES.LIKESHOP]: 'instagram',
  [DATA_SOURCES.TIKTOK_LIKESHOP]: 'tiktok',
});

export const AD_SOURCE_PLATFORM_MEDIA_TYPE_MAPPING = Object.freeze({
  [AD_SOURCES.META_ADS]: {
    photo: 'ads-image',
    image: 'ads-image',
    video: 'ads-video',
    reel: 'ads-video',
    carousel: 'ads-carousel',
    link: 'ads-link',
    event: 'ads-event',
  },
  [AD_SOURCES.TIKTOK_ADS]: {
    SINGLE_IMAGE: 'ads-image',
    SINGLE_VIDEO: 'ads-video',
    CAROUSEL_ADS: 'ads-carousel',
    CATALOG_CAROUSEL: 'ads-catalog-carousel',
    LIVE_CONTENT: 'ads-live',
  },
});

export const AD_SOURCE_PLATFORM_CONNECTIONS = Object.freeze({
  [AD_SOURCES.META_ADS]: PLATFORM_CONNECTION.FACEBOOK_ADS_NEW,
  [AD_SOURCES.TIKTOK_ADS]: PLATFORM_CONNECTION.TIKTOK_ADS,
});

export const META_ADS_MEDIA_POST_TYPE_PRIORITY = Object.freeze({
  photo: 0,
  image: 0,
  video: 0,
  reel: 0,
  link: 1, // We should only choose link posts if there are no other ad placements
  event: 0,
});

export const AD_METRIC_TO_AD_POST_VALUE_KEY = Object.freeze({
  [AD_SOURCES.META_ADS]: {
    TOTAL_SPENT: 'spend',
    CLICKS: 'clicks',
    CONVERSION_RATE: 'conversion_rate',
    COST_PER_CLICK: 'cpc',
    COST_PER_MILE: 'cpm',
    CLICK_THROUGH_RATE: 'ctr',
    ENGAGEMENT_RATE: 'engagement_rate',
    ENGAGEMENTS: 'engagements',
    FREQUENCY: 'frequency',
    IMPRESSIONS: 'impressions',
    LINK_CLICKS: 'link_clicks',
    PAGE_ENGAGEMENTS: 'page_engagements',
    REACH: 'reach',
    RETURN_ON_AD_SPEND: 'purchase_roas',
    VIDEO_VIEWS: 'video_views',
    WEB_CONVERSIONS: 'web_conversions',
  },
  [AD_SOURCES.TIKTOK_ADS]: {
    CLICKS: 'clicks',
    COST_PER_CLICK: 'cpc',
    COST_PER_MILE: 'cpm',
    CLICK_THROUGH_RATE_DESTINATION: 'ctr',
    ENGAGED_VIEW: 'engaged_view',
    ENGAGEMENTS: 'engagements',
    FOLLOWS: 'follows',
    FREQUENCY: 'frequency',
    IMPRESSIONS: 'impressions',
    REACH: 'reach',
    REAL_TIME_CONVERSIONS: 'real_time_conversion',
    REAL_TIME_CONVERSION_RATE: 'real_time_conversion_rate_v2',
    TOTAL_SPENT: 'spend',
    VIDEO_VIEWS_P50: 'video_views_p50',
    VIDEO_VIEWS_P100: 'video_views_p100',
    VIDEO_WATCHED_6S: 'video_watched_6s',
  },
});

export const AD_SOURCE_CARD_METRICS = Object.freeze({
  [AD_SOURCES.META_ADS]: [
    {
      metric: 'COST_PER_CLICK',
      formatOptions: {
        formatOptions: { notation: 'standard' },
      },
    },
    {
      metric: 'COST_PER_MILE',
      formatOptions: {
        formatOptions: { notation: 'standard' },
      },
    },
    {
      metric: 'CLICK_THROUGH_RATE',
      normalizePercent: true,
    },
    {
      metric: 'FREQUENCY',
    },
  ],
  [AD_SOURCES.TIKTOK_ADS]: [
    {
      metric: 'COST_PER_CLICK',
      formatOptions: {
        formatOptions: { notation: 'standard' },
      },
    },
    {
      metric: 'COST_PER_MILE',
      formatOptions: {
        formatOptions: { notation: 'standard' },
      },
    },
    {
      metric: 'CLICK_THROUGH_RATE_DESTINATION',
      normalizePercent: true,
    },
    {
      metric: 'FREQUENCY',
    },
  ],
});

export const AD_STATUS = Object.freeze({
  ACTIVE: {
    label: 'Active',
    severity: 'success',
  },
  PAUSED: {
    label: 'Paused',
    severity: 'warning',
  },
  DELETED: {
    label: 'Deleted',
    severity: 'statusNeutral',
  },
  COMPLETED: {
    label: 'Completed',
    severity: 'statusNeutral',
  },
  PENDING: {
    label: 'Pending',
    severity: 'warning',
  },
  ARCHIVED: {
    label: 'Archived',
    severity: 'statusNeutral',
  },
  WITH_ISSUES: {
    label: 'With Issues',
    severity: 'danger',
  },
  OVER_BUDGET: {
    label: 'Over Budget',
    severity: 'danger',
  },
  UNKNOWN: {
    label: 'Unknown',
    severity: 'statusNeutral',
  },
});

export const AD_SOURCE_STATUS_MAPPING = Object.freeze({
  [AD_SOURCES.META_ADS]: {
    ACTIVE: AD_STATUS.ACTIVE,
    PAUSED: AD_STATUS.PAUSED,
    DELETED: AD_STATUS.DELETED,
    PENDING_REVIEW: AD_STATUS.PENDING,
    DISAPPROVED: AD_STATUS.PENDING,
    PREAPPROVED: AD_STATUS.PENDING,
    PENDING_BILLING_INFO: AD_STATUS.PENDING,
    CAMPAIGN_PAUSED: AD_STATUS.PAUSED,
    ARCHIVED: AD_STATUS.ARCHIVED,
    ADSET_PAUSED: AD_STATUS.PAUSED,
    IN_PROCESS: AD_STATUS.PENDING,
    WITH_ISSUES: AD_STATUS.WITH_ISSUES,
  },
  [AD_SOURCES.TIKTOK_ADS]: {
    AD_STATUS_ALL: AD_STATUS.PENDING,
    AD_STATUS_NOT_DELETE: AD_STATUS.PENDING,
    AD_STATUS_CAMPAIGN_DELETE: AD_STATUS.DELETED,
    AD_STATUS_ADGROUP_DELETE: AD_STATUS.DELETED,
    AD_STATUS_DELETE: AD_STATUS.DELETED,
    AD_STATUS_ADVERTISER_AUDIT_DENY: AD_STATUS.WITH_ISSUES,
    AD_STATUS_ADVERTISER_AUDIT: AD_STATUS.WITH_ISSUES,
    ADVERTISER_CONTRACT_PENDING: AD_STATUS.WITH_ISSUES,
    ADVERTISER_ACCOUNT_PUNISH: AD_STATUS.WITH_ISSUES,
    AD_STATUS_BALANCE_EXCEED: AD_STATUS.OVER_BUDGET,
    AD_STATUS_CAMPAIGN_EXCEED: AD_STATUS.OVER_BUDGET,
    AD_STATUS_BUDGET_EXCEED: AD_STATUS.OVER_BUDGET,
    AD_STATUS_AD_PRE_ONLINE: AD_STATUS.PENDING,
    AD_STATUS_AUDIT: AD_STATUS.PENDING,
    AD_STATUS_REAUDIT: AD_STATUS.PENDING,
    AD_STATUS_AUDIT_DENY: AD_STATUS.WITH_ISSUES,
    AD_STATUS_ADGROUP_AUDIT_DENY: AD_STATUS.WITH_ISSUES,
    AD_STATUS_ADGROUP_PARTIAL_AUDIT_NO_DELIVERY: AD_STATUS.PENDING,
    AD_STATUS_REVIEW_PARTIALLY_APPROVED: AD_STATUS.PENDING,
    AD_STATUS_PARTIAL_AUDIT_DELIVERY_OK: AD_STATUS.PENDING,
    AD_STATUS_PARTIAL_AUDIT_NO_DELIVERY: AD_STATUS.WITH_ISSUES,
    AD_STATUS_ADGROUP_INDUSTRY_QUALIFICATION_MISSING: AD_STATUS.WITH_ISSUES,
    AD_STATUS_ADGROUP_INDUSTRY_QUALIFICATION_EXPIRED: AD_STATUS.WITH_ISSUES,
    AD_STATUS_ADGROUP_INDUSTRY_QUALIFICATION_DENY: AD_STATUS.WITH_ISSUES,
    AD_STATUS_MUSIC_AUTHORIZATION_MISSING: AD_STATUS.WITH_ISSUES,
    AD_STAUS_PIXEL_UNBIND: AD_STATUS.WITH_ISSUES,
    AD_STATUS_LIVE_OFFLINE: AD_STATUS.PENDING,
    AD_STATUS_NOT_START: AD_STATUS.PENDING,
    AD_STATUS_DONE: AD_STATUS.COMPLETED,
    AD_STATUS_CAMPAIGN_DISABLE: AD_STATUS.WITH_ISSUES,
    AD_STATUS_ADGROUP_DISABLE: AD_STATUS.PENDING,
    ADGROUP_STATUS_FROZEN: AD_STATUS.PAUSED,
    AD_STATUS_DISABLE: AD_STATUS.WITH_ISSUES,
    AD_STATUS_DELIVERY_OK: AD_STATUS.ACTIVE,
    AD_STATUS_PROCESS_AUDIO: AD_STATUS.WITH_ISSUES,
    AD_STATUS_TRANSCODING_FAIL: AD_STATUS.WITH_ISSUES,
    AD_STATUS_ADGROUP_ASSET_AUTHORIZATION_LOST: AD_STATUS.WITH_ISSUES,
    AD_STATUS_ASSET_AUTHORIZATION_LOST: AD_STATUS.WITH_ISSUES,
    AD_STATUS_AD_QUOTA_LIMIT: AD_STATUS.PAUSED,
    AD_STATUS_PRODUCT_UNAVAILABLE: AD_STATUS.WITH_ISSUES,
    AD_STATUS_ANCHOR_UNAVAILABLE: AD_STATUS.WITH_ISSUES,
    AD_STATUS_NO_AUTHORIZATION_OF_SHOWCASE: AD_STATUS.WITH_ISSUES,
    AD_STATUS_RF_ADGROUP_CLOSED: AD_STATUS.WITH_ISSUES,
    AD_STATUS_PROMOTE_AD_OFFLINE_AUDIT: AD_STATUS.WITH_ISSUES,
    AD_STATUS_PROMOTE_ADGROUP_CLOSED: AD_STATUS.WITH_ISSUES,
  },
});

export const META_ADS_STATUS_FILTER_OPTIONS = Object.freeze([
  { value: 'ACTIVE', label: 'Active' },
  { value: 'PAUSED', label: 'Paused' },
  { value: 'DELETED', label: 'Deleted' },
  { value: 'ARCHIVED', label: 'Archived' },
  { value: 'WITH_ISSUES', label: 'With Issues' },
]);

export const META_ADS_EMPTY_STATE = Object.freeze({
  NO_FILTERED_ADS: Object.freeze({
    value: 'NO_FILTERED_ADS',
    variant: 'pageEmpty',
    icon: 'search',
    title: 'No Ads Match Your Filters',
    message: 'Adjust your filters to view more ads',
  }),
});

export const INSTAGRAM_UGC_METRICS = {
  sortOptions: [
    {
      title: 'Posts',
      children: [
        { value: 'INSTAGRAM_ENGAGEMENT_RATE', label: 'Engagement Rate', attribute: 'engagement' },
        {
          value: 'INSTAGRAM_TOTAL_ENGAGEMENTS',
          label: 'Engagements',
          attribute: 'totalEngagement',
        },
        { value: 'INSTAGRAM_TOTAL_LIKES', label: 'Likes', attribute: 'totalLikes' },
        { value: 'INSTAGRAM_REACH', label: 'Reach', attribute: 'reach' },
        { value: 'INSTAGRAM_PUBLIC_VIEWS', label: 'Views', attribute: 'publicViews' },
      ],
    },
    {
      title: 'Stories',
      children: [
        {
          value: 'INSTAGRAM_STORY_FRAME_COMPLETION_RATE_VIEWS',
          label: 'Completion Rate',
          attribute: 'completionRateViews',
        },
        {
          value: 'INSTAGRAM_STORY_FRAME_EXIT_RATE_VIEWS',
          label: 'Exit Rate',
          attribute: 'exitRateViews',
        },
        { value: 'INSTAGRAM_STORY_FRAME_EXITS', label: 'Exits', attribute: 'exits' },
        { value: 'INSTAGRAM_STORY_FRAME_REACH', label: 'Reach', attribute: 'reach' },
        { value: 'INSTAGRAM_STORY_FRAME_VIEWS_V2', label: 'Views', attribute: 'viewsV2' },
      ],
    },
    {
      title: 'Discontinued',
      children: [
        {
          value: 'INSTAGRAM_STORY_FRAME_COMPLETION_RATE',
          label: 'Completion Rate (Discontinued)',
          attribute: 'completionRate',
        },
        {
          value: 'INSTAGRAM_STORY_FRAME_EXIT_RATE',
          label: 'Exit Rate (Discontinued)',
          attribute: 'exitRate',
        },
        {
          value: 'INSTAGRAM_STORY_FRAME_IMPRESSIONS',
          label: 'Impressions (Discontinued)',
          attribute: 'views',
        },
      ],
    },
  ],
  displayMetrics: [
    { label: 'Estimated Reach', attribute: 'reach' },
    { label: 'Engagement Rate', attribute: 'engagement' },
    { label: 'Engagements - Organic', attribute: 'totalEngagement' },
    { label: 'Likes', attribute: 'totalLikes' },
    { label: 'Views - Total', attribute: 'publicViews' },
  ],
  storiesDisplayMetrics: [
    { label: 'Estimated Reach', attribute: 'reach' },
    { label: 'Views', attribute: 'viewsV2' },
    { label: 'Completion Rate', attribute: 'completionRateViews' },
    { label: 'Exit Rate', attribute: 'exitRateViews' },
  ],
  metricLabels: [
    { label: 'Estimated Reach', attribute: 'reach' },
    { label: 'Engagement Rate', attribute: 'engagement' },
    { label: 'Engagements - Organic', attribute: 'totalEngagement' },
    { label: 'Likes', attribute: 'totalLikes' },
    { label: 'Mentioned You', attribute: 'isMentioned' },
    { label: 'Tagged You', attribute: 'isTagged' },
    { label: 'Impressions (Discontinued)', attribute: 'views' },
    { label: 'Completion Rate (Discontinued)', attribute: 'completionRate' },
    { label: 'Exit Rate (Discontinued)', attribute: 'exitRate' },
    { label: 'Completion Rate', attribute: 'completionRateViews' },
    { label: 'Exit Rate', attribute: 'exitRateViews' },
    { label: 'Exits', attribute: 'exits' },
    { label: 'Views - Total', attribute: 'publicViews' },
    { label: 'Views', attribute: 'viewsV2' },
  ],
};

const TIKTOK_UGC_SORT_OPTIONS = [
  { value: 'TIKTOK_VIEWS', label: 'Video Views', attribute: 'views' },
  { value: 'TIKTOK_REACH', label: 'Reach', attribute: 'reach' },
  { value: 'TIKTOK_TOTAL_ENGAGEMENTS', label: 'Engagements', attribute: 'totalEngagements' },
  { value: 'TIKTOK_ENGAGEMENT_RATE', label: 'Engagement Rate', attribute: 'engagementRate' },
  { value: 'TIKTOK_LIKES', label: 'Likes', attribute: 'likes' },
  { value: 'TIKTOK_COMMENTS', label: 'Comments', attribute: 'comments' },
  { value: 'TIKTOK_SHARES', label: 'Shares', attribute: 'shares' },
  { value: 'TIKTOK_FAVORITES', label: 'Favorites', attribute: 'favorites' },
  { value: 'TIKTOK_EMV', label: 'EMV', attribute: 'emv' },
];

export const TIKTOK_UGC_METRICS = {
  sortOptions: TIKTOK_UGC_SORT_OPTIONS,
  displayMetrics: [
    { label: 'Video Views', attribute: 'views' },
    { label: 'Likes', attribute: 'likes' },
    { label: 'Comments', attribute: 'comments' },
    { label: 'EMV', attribute: 'emv' },
  ],
  metricLabels: TIKTOK_UGC_SORT_OPTIONS.map(({ label, attribute }) => ({ label, attribute })),
};

export const RELATIONSHIPS_METRICS = {
  displayMetrics: [
    { label: 'Engagement Rate', attribute: 'engagement' },
    { label: 'Effectiveness', attribute: 'effectiveness' },
    { label: 'Followers Gained', attribute: 'followersGained' },
    { label: 'EMV', attribute: 'emv' },
  ],
  metricLabels: [
    { label: 'Engagement Rate', attribute: 'engagement' },
    { label: 'Effectiveness', attribute: 'effectiveness' },
    { label: 'Followers Gained', attribute: 'followersGained' },
    { label: 'EMV', attribute: 'emv' },
    { label: 'Reach', attribute: 'reach' },
    { label: 'Views', attribute: 'views' },
    { label: 'Completion Rate', attribute: 'completionRateViews' },
    { label: 'Exit Rate', attribute: 'exitRateViews' },
  ],
};

export const verticalMediaPanelCopy = {
  campaignsTopPerformingVideos: {
    title: 'Top Performing Videos - Owned',
    tooltip:
      'The top performing videos in this Campaign across all owned channels, sorted by video views',
    emptyMessage: 'No videos found during this reporting period',
  },
  campaignsLowestPerformingVideos: {
    title: 'Lowest Performing Videos - Owned',
    tooltip:
      'The lowest performing videos in this Campaign across all owned channels, sorted by video views ',
    emptyMessage: 'No videos found during this reporting period',
  },
  campaignsTopPerformingPosts: {
    title: 'Top Performing Posts - Owned',
    tooltip: 'The top performing posts in this Campaign across all owned channels',
    emptyMessage: 'No posts found during this reporting period',
  },
  campaignsLowestPerformingPosts: {
    title: 'Lowest Performing Posts - Owned',
    tooltip: 'The lowest performing posts in this Campaign across all owned channels',
    emptyMessage: 'No posts found during this reporting period',
  },
};

export const videoViewsAliasMap = {
  INSTAGRAM: 'video_views',
  TIKTOK: 'views',
  FACEBOOK: 'video_views',
  PINTEREST: 'total_video_views',
  TWITTER: 'video_views',
  INSTAGRAM_STORY: 'views',
  YOUTUBE: 'views',
};

export const videoSortOptions = {
  CROSS_CHANNEL_VIDEO_VIEWS: 'CROSS_CHANNEL_VIDEO_VIEWS',
};

export const graphMetrics = {
  POSTS: {
    value: 'NUMBER_OF_MEDIA',
    text: 'Posts',
  },
  NUMBER_OF_MEDIA: {
    value: 'NUMBER_OF_MEDIA',
    text: 'Posts',
  },
  NET_NEW_FOLLOWERS: {
    value: 'NET_NEW_FOLLOWERS',
    text: 'Net New Followers',
  },
  VIDEO_VIEWS: {
    value: 'VIDEO_VIEWS',
    text: 'Video Views',
  },
  TOTAL_VIDEO_VIEWS: {
    value: 'TOTAL_VIDEO_VIEWS',
    text: 'Video Views',
  },
  VIEWS_ONLY_VIDEOS: {
    value: 'VIEWS_ONLY_VIDEOS',
    text: 'Video Views',
  },
  IMPRESSIONS: {
    value: 'IMPRESSIONS',
    text: 'Impressions',
  },
  Views: {
    value: 'VIEWS',
    text: 'Views',
  },
  LINK_CLICKS: {
    value: 'LINK_CLICKS',
    text: 'Link Clicks',
  },
  SUBSCRIBERS_GAINED: {
    value: 'SUBSCRIBERS_GAINED',
    text: 'Subscribers Gained',
  },
  SUBSCRIBERS_LOST: {
    value: 'SUBSCRIBERS_LOST',
    text: 'Subscribers Lost',
  },
};

// Maps each hand-selected channel time-series graph (keyed by its legacy campaign-stats
// metric) to the equivalent dashboards metric fetched with report_type=GRAPH. Used when
// the campaigns-custom-kpi-cards flag is on. Each key/value pair is verified GRAPH-compatible
// against dashboard-backend's per-channel graph_source_list. The BE XLSX graphs page reuses
// this same graph -> metric mapping (see sc-203672 / sc-203663).
export const CAMPAIGN_GRAPH_DASHBOARDS_METRICS = Object.freeze({
  [CHANNELS.FACEBOOK]: Object.freeze({
    [graphMetrics.NUMBER_OF_MEDIA.value]: 'NUMBER_OF_POSTS',
    [graphMetrics.VIEWS_ONLY_VIDEOS.value]: 'PAID_AND_ORGANIC_VIEWS_BY_POST_VIDEO_REEL',
  }),
  [CHANNELS.INSTAGRAM]: Object.freeze({
    [graphMetrics.NUMBER_OF_MEDIA.value]: 'NUMBER_OF_POSTS',
    [graphMetrics.VIEWS_ONLY_VIDEOS.value]: 'VIEWS_VIDEO_REEL',
  }),
  [CHANNELS.INSTAGRAM_STORY]: Object.freeze({
    [graphMetrics.Views.value]: 'VIEWS',
    [graphMetrics.LINK_CLICKS.value]: 'SWIPE_UPS',
  }),
  [CHANNELS.TWITTER]: Object.freeze({
    [graphMetrics.NUMBER_OF_MEDIA.value]: 'NUMBER_OF_POSTS',
    [graphMetrics.VIDEO_VIEWS.value]: 'TOTAL_VIDEO_VIEWS',
  }),
  [CHANNELS.TIKTOK]: Object.freeze({
    [graphMetrics.NUMBER_OF_MEDIA.value]: 'NUMBER_OF_POSTS',
    [graphMetrics.VIDEO_VIEWS.value]: 'TOTAL_VIDEO_VIEWS',
  }),
  [CHANNELS.PINTEREST]: Object.freeze({
    [graphMetrics.NUMBER_OF_MEDIA.value]: 'NUMBER_OF_POSTS',
    [graphMetrics.VIDEO_VIEWS.value]: 'TOTAL_VIDEO_VIEWS',
  }),
  [CHANNELS.YOUTUBE]: Object.freeze({
    [graphMetrics.VIDEO_VIEWS.value]: 'TOTAL_VIDEO_VIEWS',
  }),
});

// Flattened fetch plan for the mapping above: one entry per dashboards GRAPH metric with
// the backend channels it should be requested for. Each metric-channel pair is fetched in
// its own concurrent report_type=GRAPH call, because dashboards rejects a multi-channel
// request outright unless the metric is comparable across every channel in it. Channels
// use the dashboards (backend) enum values.
export const CAMPAIGN_GRAPH_METRIC_FETCH_PLAN = Object.freeze(
  Object.entries(
    Object.entries(CAMPAIGN_GRAPH_DASHBOARDS_METRICS).reduce((plan, [channel, metricMap]) => {
      const backendChannel = FRONTEND_TO_BACKEND_CHANNEL_MAP[channel] ?? channel;
      Object.values(metricMap).forEach((dashboardsMetric) => {
        (plan[dashboardsMetric] ??= new Set()).add(backendChannel);
      });
      return plan;
    }, {}),
  ).map(([metric, channels]) => Object.freeze({ metric, channels: Object.freeze([...channels]) })),
);

export const postsDefaultSortOption = 'CROSS_CHANNEL_ENGAGEMENT_RATE';

export const campaignRouteNames = /** @type {const} */ Object.freeze({
  CAMPAIGNS: 'campaigns',
  CAMPAIGNS_CREATE: 'campaigns.create',
  CAMPAIGN_DETAIL: 'campaigns.detail',
  CAMPAIGN_DETAIL_EDIT: 'campaigns.detail.edit',
  CAMPAIGN_DETAIL_DELETE: 'campaigns.detail.delete',
  CAMPAIGN_DETAIL_OVERVIEW: 'campaigns.detail.overview',
  CAMPAIGN_DETAIL_OWNED_SUMMARY: 'campaigns.detail.owned-summary',
  CAMPAIGN_DETAIL_SOCIAL_ADS_SUMMARY: 'campaigns.detail.social-ads-summary',
  CAMPAIGN_DETAIL_CREATOR_SUMMARY: 'campaigns.detail.creator-summary',
  CAMPAIGN_DETAIL_TIKTOK: 'campaigns.detail.tiktok',
  CAMPAIGN_DETAIL_INSTAGRAM: 'campaigns.detail.instagram',
  CAMPAIGN_DETAIL_INSTAGRAM_STORIES: 'campaigns.detail.instagram-stories',
  CAMPAIGN_DETAIL_FACEBOOK: 'campaigns.detail.facebook',
  CAMPAIGN_DETAIL_PINTEREST: 'campaigns.detail.pinterest',
  CAMPAIGN_DETAIL_TWITTER: 'campaigns.detail.twitter',
  CAMPAIGN_DETAIL_YOUTUBE: 'campaigns.detail.youtube',
  CAMPAIGN_DETAIL_ECOMM: 'campaigns.detail.ecomm',
  CAMPAIGN_DETAIL_GOOGLE_ANALYTICS: 'campaigns.detail.google-analytics',
  CAMPAIGN_DETAIL_RELATIONSHIPS: 'campaigns.detail.relationships',
  CAMPAIGN_DETAIL_UGC: 'campaigns.detail.ugc',
  CAMPAIGN_DETAIL_TIKTOK_UGC: 'campaigns.detail.tiktok-ugc',
  CAMPAIGN_DETAIL_META_ADS: 'campaigns.detail.meta-ads',
  CAMPAIGN_DETAIL_TIKTOK_ADS: 'campaigns.detail.tiktok-ads',
  CAMPAIGN_DETAIL_CREATOR_INSIGHTS: 'campaigns.detail.creator-insights',
  CAMPAIGN_DETAIL_CREATOR_DELIVERABLES: 'campaigns.detail.creator-deliverables',
  CAMPAIGN_DETAIL_REPORT: 'campaigns.detail.report',
  CAMPAIGN_DETAIL_OVERVIEW_REPORT: 'campaigns.detail.overview.report',
  CAMPAIGN_DETAIL_OWNED_SUMMARY_REPORT: 'campaigns.detail.owned-summary.report',
  CAMPAIGN_DETAIL_SOCIAL_ADS_SUMMARY_REPORT: 'campaigns.detail.social-ads-summary.report',
  CAMPAIGN_DETAIL_CREATOR_SUMMARY_REPORT: 'campaigns.detail.creator-summary.report',
  CAMPAIGN_DETAIL_TIKTOK_REPORT: 'campaigns.detail.tiktok.report',
  CAMPAIGN_DETAIL_INSTAGRAM_REPORT: 'campaigns.detail.instagram.report',
  CAMPAIGN_DETAIL_INSTAGRAM_STORIES_REPORT: 'campaigns.detail.instagram-stories.report',
  CAMPAIGN_DETAIL_FACEBOOK_REPORT: 'campaigns.detail.facebook.report',
  CAMPAIGN_DETAIL_PINTEREST_REPORT: 'campaigns.detail.pinterest.report',
  CAMPAIGN_DETAIL_TWITTER_REPORT: 'campaigns.detail.twitter.report',
  CAMPAIGN_DETAIL_YOUTUBE_REPORT: 'campaigns.detail.youtube.report',
  CAMPAIGN_DETAIL_ECOMM_REPORT: 'campaigns.detail.ecomm.report',
  CAMPAIGN_DETAIL_GOOGLE_ANALYTICS_REPORT: 'campaigns.detail.google-analytics.report',
  CAMPAIGN_DETAIL_RELATIONSHIPS_REPORT: 'campaigns.detail.relationships.report',
  CAMPAIGN_DETAIL_UGC_REPORT: 'campaigns.detail.ugc.report',
  CAMPAIGN_DETAIL_TIKTOK_UGC_REPORT: 'campaigns.detail.tiktok-ugc.report',
  CAMPAIGN_DETAIL_META_ADS_REPORT: 'campaigns.detail.meta-ads.report',
  CAMPAIGN_DETAIL_TIKTOK_ADS_REPORT: 'campaigns.detail.tiktok-ads.report',
  CAMPAIGN_DETAIL_CREATOR_INSIGHTS_REPORT: 'campaigns.detail.creator-insights.report',
  CAMPAIGN_DETAIL_CREATOR_DELIVERABLES_REPORT: 'campaigns.detail.creator-deliverables.report',
  CAMPAIGN_DETAIL_ALL_TABS_REPORT: 'campaigns.detail.all.report',
});

export const AD_SOURCE_TO_ROUTE_NAME = {
  [AD_SOURCES.META_ADS]: campaignRouteNames.CAMPAIGN_DETAIL_META_ADS,
  [AD_SOURCES.TIKTOK_ADS]: campaignRouteNames.CAMPAIGN_DETAIL_TIKTOK_ADS,
};

export const CAMPAIGN_TAB_TYPES = Object.freeze({
  OWNED: 'OWNED',
  CREATORS: 'CREATORS',
  ADS: 'ADS',
  ANALYTICS: 'ANALYTICS',
  OTHER: 'OTHER',
});

export const CAMPAIGN_TABS = Object.freeze({
  [CAMPAIGN_PAGE.OWNED_SUMMARY]: 'owned-summary',
  [CAMPAIGN_PAGE.SOCIAL_ADS_SUMMARY]: 'social-ads-summary',
  [CAMPAIGN_PAGE.CREATOR_SUMMARY]: 'creator-summary',
  [CAMPAIGN_PAGE.CREATOR_DELIVERABLES]: 'creator-deliverables',
  [CAMPAIGN_PAGE.TIKTOK]: 'tiktok',
  [CAMPAIGN_PAGE.INSTAGRAM]: 'instagram',
  [CAMPAIGN_PAGE.INSTAGRAM_STORIES]: 'instagram-stories',
  [CAMPAIGN_PAGE.FACEBOOK]: 'facebook',
  [CAMPAIGN_PAGE.PINTEREST]: 'pinterest',
  [CAMPAIGN_PAGE.X]: 'twitter',
  [CAMPAIGN_PAGE.YOUTUBE]: 'youtube',
  [CAMPAIGN_PAGE.RELATIONSHIPS]: 'relationships',
  [CAMPAIGN_PAGE.INSTAGRAM_UGC]: 'ugc',
  [CAMPAIGN_PAGE.TIKTOK_UGC]: 'tiktok-ugc',
  [CAMPAIGN_PAGE.META_ADS]: 'meta-ads',
  [CAMPAIGN_PAGE.TIKTOK_ADS]: 'tiktok-ads',
  [CAMPAIGN_PAGE.GOOGLE_ANALYTICS]: 'google-analytics',
  ALL: 'all',
});

export const CAMPAIGN_TAB_CONFIG = Object.freeze({
  [CAMPAIGN_TABS.OWNED_SUMMARY]: {
    label: 'Owned Summary',
    type: CAMPAIGN_TAB_TYPES.OWNED,
    showScalePicker: false,
    showEmptyState: true,
    link: campaignRouteNames.CAMPAIGN_DETAIL_OWNED_SUMMARY,
    reportLink: campaignRouteNames.CAMPAIGN_DETAIL_OWNED_SUMMARY_REPORT,
  },
  [CAMPAIGN_TABS.SOCIAL_ADS_SUMMARY]: {
    label: 'Social Ads Summary',
    type: CAMPAIGN_TAB_TYPES.ADS,
    showScalePicker: false,
    showEmptyState: false,
    link: campaignRouteNames.CAMPAIGN_DETAIL_SOCIAL_ADS_SUMMARY,
    reportLink: campaignRouteNames.CAMPAIGN_DETAIL_SOCIAL_ADS_SUMMARY_REPORT,
  },
  [CAMPAIGN_TABS.CREATOR_SUMMARY]: {
    label: 'Creator Summary',
    type: CAMPAIGN_TAB_TYPES.CREATORS,
    showScalePicker: false,
    showEmptyState: false,
    link: campaignRouteNames.CAMPAIGN_DETAIL_CREATOR_SUMMARY,
    reportLink: campaignRouteNames.CAMPAIGN_DETAIL_CREATOR_SUMMARY_REPORT,
  },
  [CAMPAIGN_TABS.CREATOR_DELIVERABLES]: {
    label: 'Creator Deliverables',
    type: CAMPAIGN_TAB_TYPES.CREATORS,
    showScalePicker: false,
    showEmptyState: false,
    link: campaignRouteNames.CAMPAIGN_DETAIL_CREATOR_DELIVERABLES,
    reportLink: campaignRouteNames.CAMPAIGN_DETAIL_CREATOR_DELIVERABLES_REPORT,
  },
  [CAMPAIGN_TABS.TIKTOK]: {
    label: 'TikTok',
    type: CAMPAIGN_TAB_TYPES.OWNED,
    showScalePicker: true,
    showEmptyState: true,
    link: campaignRouteNames.CAMPAIGN_DETAIL_TIKTOK,
    reportLink: campaignRouteNames.CAMPAIGN_DETAIL_TIKTOK_REPORT,
  },
  [CAMPAIGN_TABS.INSTAGRAM]: {
    label: 'Instagram',
    type: CAMPAIGN_TAB_TYPES.OWNED,
    showScalePicker: true,
    showEmptyState: true,
    link: campaignRouteNames.CAMPAIGN_DETAIL_INSTAGRAM,
    reportLink: campaignRouteNames.CAMPAIGN_DETAIL_INSTAGRAM_REPORT,
  },
  [CAMPAIGN_TABS.INSTAGRAM_STORIES]: {
    label: 'Instagram Stories',
    type: CAMPAIGN_TAB_TYPES.OWNED,
    showScalePicker: true,
    showEmptyState: true,
    link: campaignRouteNames.CAMPAIGN_DETAIL_INSTAGRAM_STORIES,
    reportLink: campaignRouteNames.CAMPAIGN_DETAIL_INSTAGRAM_STORIES_REPORT,
  },
  [CAMPAIGN_TABS.FACEBOOK]: {
    label: 'Facebook',
    type: CAMPAIGN_TAB_TYPES.OWNED,
    showScalePicker: true,
    showEmptyState: true,
    link: campaignRouteNames.CAMPAIGN_DETAIL_FACEBOOK,
    reportLink: campaignRouteNames.CAMPAIGN_DETAIL_FACEBOOK_REPORT,
  },
  [CAMPAIGN_TABS.PINTEREST]: {
    label: 'Pinterest',
    type: CAMPAIGN_TAB_TYPES.OWNED,
    showScalePicker: true,
    showEmptyState: true,
    link: campaignRouteNames.CAMPAIGN_DETAIL_PINTEREST,
    reportLink: campaignRouteNames.CAMPAIGN_DETAIL_PINTEREST_REPORT,
  },
  [CAMPAIGN_TABS.X]: {
    label: 'X',
    type: CAMPAIGN_TAB_TYPES.OWNED,
    showScalePicker: true,
    showEmptyState: true,
    link: campaignRouteNames.CAMPAIGN_DETAIL_TWITTER,
    reportLink: campaignRouteNames.CAMPAIGN_DETAIL_TWITTER_REPORT,
  },
  [CAMPAIGN_TABS.YOUTUBE]: {
    label: 'YouTube',
    type: CAMPAIGN_TAB_TYPES.OWNED,
    showScalePicker: true,
    showEmptyState: true,
    link: campaignRouteNames.CAMPAIGN_DETAIL_YOUTUBE,
    reportLink: campaignRouteNames.CAMPAIGN_DETAIL_YOUTUBE_REPORT,
  },
  [CAMPAIGN_TABS.RELATIONSHIPS]: {
    label: 'Relationships',
    type: CAMPAIGN_TAB_TYPES.OTHER,
    showScalePicker: false,
    showEmptyState: false,
    link: campaignRouteNames.CAMPAIGN_DETAIL_RELATIONSHIPS,
    reportLink: campaignRouteNames.CAMPAIGN_DETAIL_RELATIONSHIPS_REPORT,
  },
  [CAMPAIGN_TABS.INSTAGRAM_UGC]: {
    label: 'Instagram UGC',
    type: CAMPAIGN_TAB_TYPES.OTHER,
    showScalePicker: false,
    showEmptyState: false,
    link: campaignRouteNames.CAMPAIGN_DETAIL_UGC,
    reportLink: campaignRouteNames.CAMPAIGN_DETAIL_UGC_REPORT,
  },
  [CAMPAIGN_TABS.TIKTOK_UGC]: {
    label: 'TikTok UGC',
    type: CAMPAIGN_TAB_TYPES.OTHER,
    showScalePicker: false,
    showEmptyState: false,
    link: campaignRouteNames.CAMPAIGN_DETAIL_TIKTOK_UGC,
    reportLink: campaignRouteNames.CAMPAIGN_DETAIL_TIKTOK_UGC_REPORT,
  },
  [CAMPAIGN_TABS.META_ADS]: {
    label: 'Meta Ads',
    type: CAMPAIGN_TAB_TYPES.ADS,
    showScalePicker: true,
    showEmptyState: false,
    link: campaignRouteNames.CAMPAIGN_DETAIL_META_ADS,
    reportLink: campaignRouteNames.CAMPAIGN_DETAIL_META_ADS_REPORT,
  },
  [CAMPAIGN_TABS.TIKTOK_ADS]: {
    label: 'TikTok Ads',
    type: CAMPAIGN_TAB_TYPES.ADS,
    showScalePicker: true,
    showEmptyState: false,
    link: campaignRouteNames.CAMPAIGN_DETAIL_TIKTOK_ADS,
    reportLink: campaignRouteNames.CAMPAIGN_DETAIL_TIKTOK_ADS_REPORT,
  },
  [CAMPAIGN_TABS.GOOGLE_ANALYTICS]: {
    label: 'Google Analytics',
    type: CAMPAIGN_TAB_TYPES.ANALYTICS,
    showScalePicker: false,
    showEmptyState: false,
    link: campaignRouteNames.CAMPAIGN_DETAIL_GOOGLE_ANALYTICS,
    reportLink: campaignRouteNames.CAMPAIGN_DETAIL_GOOGLE_ANALYTICS_REPORT,
  },
  [CAMPAIGN_TABS.ALL]: {
    showEmptyState: false,
  },
});

export const CAMPAIGN_TAB_CATEGORIES = Object.freeze({
  OWNED: 'OWNED',
  CREATORS: 'CREATORS',
  ADS: 'ADS',
});

export const CAMPAIGN_TAB_CATEGORY_LABELS = Object.freeze({
  [CAMPAIGN_TAB_CATEGORIES.OWNED]: 'Owned Channels',
  [CAMPAIGN_TAB_CATEGORIES.CREATORS]: 'Creator Channels',
  [CAMPAIGN_TAB_CATEGORIES.ADS]: 'Social Advertising Channels',
});

export const CAMPAIGN_SENTIMENT_CHANNEL_DISTRIBUTION_PLATFORMS = [
  CHANNELS.TIKTOK,
  CHANNELS.INSTAGRAM,
  CHANNELS.FACEBOOK,
  CHANNELS.TWITTER,
  CHANNELS.YOUTUBE,
];

export const CAMPAIGN_PLATFORM_LABELS = {
  [CHANNELS.TIKTOK]: 'TikTok',
  [CHANNELS.INSTAGRAM]: 'Instagram',
  [CHANNELS.FACEBOOK]: 'Facebook',
  [CHANNELS.TWITTER]: 'X',
  [CHANNELS.YOUTUBE]: 'YouTube',
  [CHANNELS.PINTEREST]: 'Pinterest',
  [CHANNELS.INSTAGRAM_STORY]: 'Instagram Story',
  INSTAGRAM_STORIES: 'Instagram Story',
  [CHANNELS.RELATIONSHIPS]: 'Relationships',
  [CHANNELS.UGC]: 'UGC',
  [AD_SOURCES.META_ADS]: 'Meta Ads',
  [AD_SOURCES.TIKTOK_ADS]: 'TikTok Ads',
  [DATA_SOURCES.INSTAGRAM_CREATORS]: 'Instagram Creators',
  [DATA_SOURCES.TIKTOK_CREATORS]: 'TikTok Creators',
  INSTAGRAM_UGC: 'Instagram UGC',
  [INSTAGRAM_STORY_UGC_SOURCE]: 'Instagram Story UGC',
  [CHANNELS.TIKTOK_UGC]: 'TikTok UGC',
  LIKESHOP: 'Instagram LikeShop',
  TIKTOK_LIKESHOP: 'TikTok LikeShop',
};

export const campaignCreatorStatusOptions = [
  { label: 'New', value: 'NEW', severity: 'statusNeutral' },
  { label: 'Contacted', value: 'CONTACTED', severity: 'statusNeutral' },
  { label: 'Agreement Sent', value: 'AGREEMENT_SENT', severity: 'info' },
  { label: 'Brief Sent', value: 'BRIEF_SENT', severity: 'info' },
  { label: 'Product Shipped', value: 'PRODUCT_SHIPPED', severity: 'info' },
  { label: 'In Progress', value: 'IN_PROGRESS', severity: 'info' },
  { label: 'Pending', value: 'PAYMENT_INITIATED', severity: 'info' },
  { label: 'Creative Approved', value: 'CREATIVE_APPROVED', severity: 'info' },
  { label: 'Paid', value: 'PAID', severity: 'success' },
  { label: 'Complete', value: 'COMPLETE', severity: 'success' },
  { label: 'Declined', value: 'DECLINED', severity: 'negative' },
];

export const limitedCampaignCreatorStatusOptions = [
  { label: 'New', value: 'NEW', severity: 'statusNeutral' },
  { label: 'Contacted', value: 'CONTACTED', severity: 'statusNeutral' },
  { label: 'Product Shipped', value: 'PRODUCT_SHIPPED', severity: 'info' },
  { label: 'Complete', value: 'COMPLETE', severity: 'success' },
  { label: 'Declined', value: 'DECLINED', severity: 'negative' },
];

export const giftOrderStatusChips = {
  [GIFT_ORDER_STATUS.ORDER_PLACED]: {
    label: 'Order Placed',
    severity: 'success',
    tooltip: 'Order confirmed and sent to the distribution center for fulfillment.',
  },
  [GIFT_ORDER_STATUS.FAILED]: {
    label: 'Order Failed',
    severity: 'danger',
    tooltip: "The order couldn't be processed. Review the items and try again",
  },
};

export const giftOrderStatusTooltips = {
  addEmail: 'Creator email is missing. Go to Creator Profile to add it in the Creator Details.',
  invite: 'Creator will receive their unique gifting link once invite sent',
  awaitingOrder: 'Waiting for the creator to submit their gift order',
  placeOrder: 'Place the order for this creator so their products can be fulfilled',
  placeOrderDisabled:
    'Resolve the out-of-stock, deleted, or unlisted product before placing the order',
  processing: 'Sending the order to Salesforce. This may take a moment',
  bulkInvite: 'Creators will receive their unique gifting link once the invite is sent',
  bulkInviteBlocked: 'None of the selected creators need a gifting invite',
  bulkPlaceOrder: 'Place the orders for the selected creators so their products can be fulfilled',
  bulkPlaceOrderBlocked:
    'Some selected creators have no order to place yet, or have a product to resolve first',
  bulkPlaceOrderTooMany: `You can place up to ${APPROVE_ORDERS_MAX} orders at a time`,
};

export const deleteGiftOrderProductConfirmation = {
  header: 'Delete Product?',
  message:
    "This product will be permanently removed from this order only. Other orders won't be affected.",
  acceptLabel: 'Delete Product',
};

export const placeGiftOrdersConfirmation = {
  single: {
    header: 'Place Order?',
    message:
      "This order will be submitted for processing, which may take a few minutes. Once placed, it can't be edited. If it fails, we'll notify you on this page so you can try again.",
    acceptLabel: 'Place Order',
  },
  plural: {
    header: 'Place Orders?',
    message:
      "These orders will be submitted for processing, which may take a few minutes. Once placed, they can't be edited. If they fail, we'll notify you on this page so you can try again.",
    acceptLabel: 'Place Orders',
  },
};

const campaignCreatorPostTypeOptions = [
  { label: 'TikTok Videos', value: 'TIKTOK_VIDEO', icon: 'tiktok-color' },
  { label: 'Instagram Feed', value: 'INSTAGRAM_FEED', icon: 'instagram-color' },
  { label: 'Instagram Reels', value: 'INSTAGRAM_REELS', icon: 'instagram-reel-color' },
  { label: 'Instagram Stories', value: 'INSTAGRAM_STORIES', icon: 'instagram-story-color' },
  { label: 'YouTube Videos', value: 'YOUTUBE_VIDEO', icon: 'youtube-color' },
  { label: 'YouTube Shorts', value: 'YOUTUBE_SHORT', icon: 'youtube-short-color' },
];

const campaignCreatorChannelOptions = [
  { label: 'TikTok', value: 'TIKTOK', icon: 'tiktok-color' },
  { label: 'Instagram', value: 'INSTAGRAM', icon: 'instagram-color' },
  { label: 'YouTube', value: 'YOUTUBE', icon: 'youtube-color' },
];

export const campaignCreatorDeliverableFilterDetails = {
  scope: 'creatorDeliverables',
  options: {
    channel: campaignCreatorChannelOptions,
    postType: campaignCreatorPostTypeOptions,
    status: campaignCreatorStatusOptions,
  },
  default: {
    channel: [],
    postType: [],
    tags: [],
    status: [],
  },
};

export const campaignCreatorDeliverableTopLineStats = [
  {
    title: 'Creators',
    value: 'totalCreators',
    format: 'number',
    type: 'Total',
    tooltip: 'The total number of creators added to this Campaign, including gifted and paid',
  },
  {
    title: 'Deliverables',
    value: 'totalDeliverables',
    format: 'number',
    type: 'Total',
    tooltip: 'The total number of expected creator posts as part of this Campaign',
  },
  {
    title: 'Gifted',
    value: 'totalGifted',
    format: 'number',
    type: 'Total',
    tooltip:
      'The total number of unique creators that were gifted product as part of this Campaign',
  },
  {
    title: 'Posts',
    value: 'totalPosts',
    format: 'number',
    type: 'Total',
    tooltip: 'The total number of published posts by creators as part of this Campaign',
  },
  {
    title: 'Completion',
    value: 'completion',
    format: 'percent',
    type: 'Average',
    tooltip:
      'The percentage of creators who have fulfilled their deliverables or have been marked as complete',
  },
];

export const campaignCreatorInsightsTopLineStats = [
  {
    title: 'Number of Posts',
    value: 'totalPosts',
    metric: 'total_posts',
    format: 'abbr',
    breakdownFormat: 'number',
    tooltip: 'The number of posts in this Campaign by all added creators',
    postType: 'ORGANIC',
    metricTimeFrame: 'POSTS_THIS_PERIOD',
  },
  {
    title: 'Views',
    value: 'views',
    metric: 'views',
    format: 'abbr',
    breakdownFormat: 'number',
    tooltip: 'The number of views across all creator posts in this campaign',
    postType: 'ORGANIC',
    metricTimeFrame: 'POSTS_THIS_PERIOD',
    predicate: requireFeatureFlag('campaignsCustomKpiCards'),
  },
  {
    title: 'EMV',
    value: 'emvViews',
    metric: 'emv_views',
    format: 'currency',
    formatOptions: { style: 'currency', currency: 'USD', maximumFractionDigits: 0 },
    breakdownFormat: 'currency',
    breakdownFormatOptions: { maximumFractionDigits: 0 },
    tooltip:
      'The earned media value of all posts in this Campaign.' +
      '\n\n' +
      'EMV is calculated by factoring in the channel, audience size, content type, engagement and current market values for comparable posts.',
    postType: 'ORGANIC',
    metricTimeFrame: 'POSTS_THIS_PERIOD',
    sourceBreakdowns: [
      {
        source: 'INSTAGRAM',
        value: 'emvViews',
        metric: 'emv_views',
      },
      {
        source: 'TIKTOK',
        value: 'emv',
        metric: 'emv',
      },
      {
        source: 'YOUTUBE',
        value: 'emv',
        metric: 'emv',
      },
    ],
  },
  {
    title: 'Avg. EMV',
    value: 'avgEmvViews',
    metric: 'avg_emv_views',
    format: 'currency',
    formatOptions: { style: 'currency', currency: 'USD', maximumFractionDigits: 0 },
    breakdownFormat: 'currency',
    breakdownFormatOptions: { maximumFractionDigits: 0 },
    tooltip:
      'The average earned media value per post in this Campaign.' +
      '\n\n' +
      'EMV is calculated by factoring in the channel, audience size, content type, engagement and current market values for comparable posts.',
    postType: 'ORGANIC',
    metricTimeFrame: 'POSTS_THIS_PERIOD',
    sourceBreakdowns: [
      {
        source: 'INSTAGRAM',
        value: 'avgEmvViews',
        metric: 'avg_emv_views',
      },
      {
        source: 'TIKTOK',
        value: 'avgEmv',
        metric: 'avg_emv',
      },
      {
        source: 'YOUTUBE',
        value: 'avgEmv',
        metric: 'avg_emv',
      },
    ],
  },
  {
    title: 'Likes',
    value: 'likes',
    metric: 'likes',
    format: 'abbr',
    breakdownFormat: 'number',
    tooltip: 'The number of likes across all applicable creator posts in this Campaign',
    postType: 'ORGANIC',
    metricTimeFrame: 'POSTS_THIS_PERIOD',
  },
  {
    title: 'Comments',
    value: 'comments',
    metric: 'comments',
    format: 'abbr',
    breakdownFormat: 'number',
    tooltip: 'The number of comments on all applicable creator posts in this Campaign',
    postType: 'ORGANIC',
    metricTimeFrame: 'POSTS_THIS_PERIOD',
  },
  {
    title: 'Engagements',
    value: 'engagements',
    metric: 'engagements',
    format: 'abbr',
    breakdownFormat: 'number',
    tooltip: 'The number of engagements across all creator posts in this campaign',
    postType: 'ORGANIC',
    metricTimeFrame: 'POSTS_THIS_PERIOD',
    predicate: requireFeatureFlag('campaignsCustomKpiCards'),
  },
  {
    title: 'Avg. Engagements',
    value: 'avgEngagements',
    metric: 'avg_engagements',
    format: 'abbr',
    breakdownFormat: 'number',
    tooltip: {
      content: 'The average engagements per creator post in this Campaign',
      footer:
        'Instagram Engagements = Likes + Comments + (Saves + Shares + Reposts if connected)\n\nTikTok Engagements = Likes + Comments + Shares\n\nYouTube Engagements = Likes + Comments',
    },
    postType: 'ORGANIC',
    metricTimeFrame: 'POSTS_THIS_PERIOD',
    sourceBreakdowns: [
      {
        source: 'INSTAGRAM',
        tooltip: {
          footer:
            'Avg. Engagements = Likes + Comments + (Saves + Shares + Reposts if connected) / Number of Posts',
        },
      },
      {
        source: 'TIKTOK',
        tooltip: {
          footer: 'Avg. Engagements = (Likes + Comments + Shares) / Number of Posts',
        },
      },
      {
        source: 'YOUTUBE',
        tooltip: {
          footer: 'Avg. Engagements = (Likes + Comments) / Number of Posts',
        },
      },
    ],
  },
  {
    title: 'Avg. Engagement Rate',
    value: 'avgEngagementRate',
    metric: 'avg_engagement_rate',
    format: 'percent',
    formatOptions: {
      style: 'percent',
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    },
    breakdownFormat: 'percent',
    breakdownFormatOptions: { maximumFractionDigits: 2, minimumFractionDigits: 2 },
    tooltip: {
      content: 'The average engagement rate per creator post in this Campaign',
      footer:
        'Instagram Engagement Rate = (Likes + Comments) / Followers\n\nTikTok Engagement Rate = (Likes + Comments + Shares) / Video Views\n\nYouTube Engagement Rate = (Likes + Comments) / Views',
    },
    postType: 'ORGANIC',
    metricTimeFrame: 'POSTS_THIS_PERIOD',
    sourceBreakdowns: [
      {
        source: 'INSTAGRAM',
        tooltip: {
          footer: 'Avg. Engagement Rate = (Engagements / Followers) / Number of Posts',
        },
      },
      {
        source: 'TIKTOK',
        tooltip: {
          footer: 'Avg. Engagement Rate = (Engagements / Video Views) / Number of Posts',
        },
      },
      {
        source: 'YOUTUBE',
        tooltip: {
          footer: 'Avg. Engagement Rate = (Engagements / Video Views) / Number of Posts',
        },
      },
    ],
  },
  {
    title: 'Creator Followers',
    value: 'audienceSize',
    metric: 'audience_size',
    format: 'abbr',
    breakdownFormat: 'number',
    tooltip:
      'The follower count of creators added to this Campaign across all channels. Use this metric to gauge the potential reach of your Campaign.',
    postType: 'ORGANIC',
    metricTimeFrame: 'POSTS_THIS_PERIOD',
    sourceBreakdowns: [
      {
        source: 'INSTAGRAM',
        label: 'Followers',
      },
      {
        source: 'TIKTOK',
        label: 'Followers',
      },
      {
        source: 'YOUTUBE',
        label: 'Subscribers',
      },
    ],
  },
];
export const campaignCreatorGoalsStats = campaignCreatorInsightsTopLineStats.filter(
  ({ value, predicate }) => !predicate && !['audienceSize'].includes(value),
);

export const CREATOR_SOURCE_TO_CAMPAIGN_STATS_KEY = {
  INSTAGRAM: 'instagramFeedStats',
  INSTAGRAM_STORY: 'instagramStoryStats',
  TIKTOK: 'tiktokStats',
  YOUTUBE: 'youtubeStats',
};

export const campaignCreatorInsightsInstagramChannelStats = [
  {
    label: 'Creators',
    value: 'totalCreators',
    formatType: 'formatIntegerAbbreviated',
    tooltip:
      'The total number of creators added into the campaign that have an Instagram handle added to their profile',
  },
  {
    label: 'Creator Followers',
    value: 'audienceSize',
    formatType: 'formatIntegerAbbreviated',
    tooltip:
      'The follower count of creators added to this campaign for Instagram. Use this metric to gauge the potential reach of your campaign.',
  },
  {
    label: 'EMV',
    value: 'emvViews',
    formatType: 'formatCurrency',
    tooltip:
      'The total earned media value for all Instagram posts in this campaign.' +
      '\n\n' +
      'EMV is calculated by factoring in the channel, audience size, content type, engagement and current market values for comparable posts.',
  },
  {
    label: 'Avg. EMV',
    value: 'avgEmvViews',
    formatType: 'formatCurrency',
    tooltip:
      'The average earned media value per Instagram post in this campaign.' +
      '\n\n' +
      'EMV is calculated by factoring in the channel, audience size, content type, engagement and current market values for comparable posts.',
  },
  {
    label: 'Engagements',
    value: 'engagements',
    formatType: 'formatIntegerAbbreviated',
    tooltip: {
      content: 'The total engagements across all creator Instagram posts in this campaign',
      footer: 'Instagram Engagements = Likes + Comments + (Saves + Shares + Reposts if connected)',
    },
  },
  {
    label: 'Avg. Engagements',
    value: 'avgEngagements',
    formatType: 'formatIntegerAbbreviated',
    tooltip: {
      content: 'The average engagements per creator Instagram post in this campaign',
      footer: 'Instagram Engagements = Likes + Comments + (Saves + Shares + Reposts if connected)',
    },
  },
  {
    label: 'Avg. Engagement Rate',
    value: 'avgEngagementRate',
    formatType: 'formatPercentage',
    formatOptions: { maximumFractionDigits: 2 },
    tooltip: {
      content: 'The average engagement rate per creator Instagram post in this campaign',
      footer: 'Instagram Engagement Rate = (Likes + Comments) / Followers',
    },
  },
  {
    label: 'Views - Connected',
    value: 'viewsV2',
    formatType: 'formatIntegerAbbreviated',
    tooltip:
      "The number of times connected creators' campaign content (Reels, Stories, and Posts) was displayed on a person's screen or played/replayed for at least 1 ms, including repeat views. Data is only available for posts after the connection date.",
  },
  {
    label: 'Avg. Views - Connected',
    value: 'avgViewsV2',
    formatType: 'formatIntegerAbbreviated',
    tooltip:
      "The average number of times connected creators' campaign content (Reels, Stories, and Posts) was displayed on a person's screen or played/replayed for at least 1 ms, including repeat views. Data is only available for posts after the connection date.",
  },
  {
    label: 'Reel Views',
    value: 'reelViews',
    formatType: 'formatIntegerAbbreviated',
    tooltip:
      "The number of times any creators' Reels in this campaign were played or replayed for at least 1 ms. Replays are counted after the first play within the same video session. Includes organic and paid views.",
  },
  {
    label: 'Avg. Est. Reach',
    value: 'avgReach',
    formatType: 'formatIntegerAbbreviated',
    tooltip:
      'The average reach per creator Instagram post in this campaign. For accounts that are not connected, reach is estimated.',
  },
];

export const campaignCreatorInsightsInstagramChannelStatsExpanded = [
  {
    label: 'Avg. Effectiveness',
    value: 'avgEffectivenessEngagements',
    formatType: 'formatPercentage',
    formatOptions: { maximumFractionDigits: 2 },
    tooltip: {
      content:
        'The average Effectiveness per creator post in this campaign. For accounts that are not connected, reach is estimated.',
      footer:
        'Effectiveness (Posts / Reels) = Likes + Comments (+ Saves + Shares + Reposts if connected) / Reach',
    },
  },
  {
    label: 'Followers Gained',
    value: 'followersGained',
    formatType: 'formatIntegerAbbreviated',
    tooltip:
      'The estimated number of followers that creators in this campaign have gained for you by tagging or mentioning your account in their posts',
  },
  {
    label: 'Stories - Avg. Completion Rate',
    value: 'storyAvgCompletionRateViews',
    formatType: 'formatPercentage',
    formatOptions: { maximumFractionDigits: 2 },
    tooltip: {
      content: "The average completion rate of creators' Stories in this campaign",
      footer: 'Completion Rate = (Views - Exits) / Views',
    },
  },
  {
    label: 'Stories - Avg. Reach',
    formatType: 'formatIntegerAbbreviated',
    value: 'storyAvgReach',
    tooltip: 'The average number of unique accounts reached per story by creators in this campaign',
  },
  {
    label: 'Stories - Avg. Views',
    formatType: 'formatIntegerAbbreviated',
    value: 'storyAvgViews',
    tooltip: 'The average number of views per creator Instagram Story in this campaign',
  },
];

export const campaignCreatorInsightsTikTokChannelStats = [
  {
    label: 'Creators',
    value: 'totalCreators',
    formatType: 'formatIntegerAbbreviated',
    tooltip:
      'The total number of creators added into the campaign that have an TikTok handle added to their profile',
  },
  {
    label: 'Creator Followers',
    value: 'audienceSize',
    formatType: 'formatIntegerAbbreviated',
    tooltip:
      'The follower count of creators added to this campaign for TikTok. Use this metric to gauge the potential reach of your campaign.',
  },
  {
    label: 'EMV',
    value: 'emv',
    formatType: 'formatCurrency',
    tooltip:
      'The earned media value of all TikTok posts in this campaign.' +
      '\n\n' +
      'EMV is calculated by factoring in the channel, audience size, content type, engagement and current market values for comparable posts.',
  },
  {
    label: 'Avg. EMV',
    value: 'avgEmv',
    formatType: 'formatCurrency',
    tooltip:
      'The average earned media value per TikTok post in this campaign.' +
      '\n\n' +
      'EMV is calculated by factoring in the channel, audience size, content type, engagement and current market values for comparable posts.',
  },
  {
    label: 'Engagements',
    value: 'engagements',
    formatType: 'formatIntegerAbbreviated',
    tooltip: {
      content: 'The total engagements across all creator TikTok posts in this campaign',
      footer: 'TikTok Engagements = Likes + Comments + Shares',
    },
  },
  {
    label: 'Avg. Engagements',
    value: 'avgEngagements',
    formatType: 'formatIntegerAbbreviated',
    tooltip: {
      content: 'The average engagements per creator TikTok post in this campaign',
      footer: 'TikTok Engagements = Likes + Comments + Shares',
    },
  },
  {
    label: 'Avg. Engagement Rate',
    value: 'avgEngagementRate',
    formatType: 'formatPercentage',
    formatOptions: { maximumFractionDigits: 2 },
    tooltip: {
      content: 'The average engagement rate per creator TikTok post in this campaign',
      footer: 'TikTok Engagement Rate = (Likes + Comments + Shares) / Video Views',
    },
  },
  {
    label: 'Video Views',
    value: 'videoViews',
    formatType: 'formatIntegerAbbreviated',
    tooltip: 'The number of video views across all creator TikTok posts in this campaign',
  },
  {
    label: 'Avg. Video Views',
    value: 'avgVideoViews',
    formatType: 'formatIntegerAbbreviated',
    tooltip: 'The average number of video views per creator TikTok post in this campaign',
  },
  {
    label: 'Shares',
    value: 'shares',
    formatType: 'formatIntegerAbbreviated',
    tooltip: 'The total shares across all creator TikTok posts in this campaign',
  },
];

export const creatorInsightPostSortOptions = {
  DEFAULT: {
    sortOrder: 'DESC',
    selected: { value: 'DATE', label: 'Recently Published' },
  },
  ALL_CHANNELS: [
    {
      value: 'DATE',
      label: 'Recently Published',
    },
    {
      value: 'CROSS_CHANNEL_EMV',
      label: 'EMV',
    },
  ],
  TIKTOK: [
    {
      value: 'DATE',
      label: 'Recently Published',
    },
    {
      value: 'TIKTOK_TOTAL_ENGAGEMENTS',
      label: 'Engagements',
    },
    {
      value: 'TIKTOK_ENGAGEMENT_RATE',
      label: 'Engagement Rate',
    },
    {
      value: 'CROSS_CHANNEL_EMV',
      label: 'EMV',
    },
    {
      value: 'TIKTOK_REACH',
      label: 'Reach',
    },
    {
      value: 'TIKTOK_SHARES',
      label: 'Shares',
    },
    {
      value: 'TIKTOK_VIEWS',
      label: 'Video Views',
    },
  ],
  INSTAGRAM: [
    {
      value: 'DATE',
      label: 'Recently Published',
    },
    {
      value: 'CROSS_CHANNEL_EMV',
      label: 'EMV',
    },
    {
      value: 'CROSS_CHANNEL_IMPRESSIONS',
      label: 'Impressions',
    },
  ],
  INSTAGRAM_FEED: [
    {
      value: 'DATE',
      label: 'Recently Published',
    },
    {
      value: 'INSTAGRAM_EFFECTIVENESS',
      label: 'Effectiveness',
    },
    {
      value: 'INSTAGRAM_ENGAGEMENT_RATE',
      label: 'Engagement Rate',
    },
    {
      value: 'INSTAGRAM_TOTAL_ENGAGEMENTS',
      label: 'Engagements',
    },
    {
      value: 'INSTAGRAM_REACH',
      label: 'Reach',
    },
    {
      value: 'INSTAGRAM_EMV',
      label: 'EMV',
    },
    {
      value: 'INSTAGRAM_FOLLOWERS_GAINED',
      label: 'Followers Gained',
    },
    {
      value: 'INSTAGRAM_TOTAL_IMPRESSIONS',
      label: 'Impressions',
    },
  ],
  INSTAGRAM_STORY: [
    {
      value: 'DATE',
      label: 'Recently Published',
    },
    {
      value: 'INSTAGRAM_STORY_FRAME_COMPLETION_RATE',
      label: 'Completion Rate',
    },
    {
      value: 'EMV',
      label: 'EMV',
    },
    {
      value: 'INSTAGRAM_STORY_FRAME_EXIT_RATE',
      label: 'Exit Rate',
    },
    {
      value: 'INSTAGRAM_STORY_FRAME_IMPRESSIONS',
      label: 'Impressions',
    },
  ],
  YOUTUBE: [
    {
      value: 'DATE',
      label: 'Recently Published',
    },
    {
      value: 'CROSS_CHANNEL_ENGAGEMENT_RATE',
      label: 'Engagement Rate',
    },
    {
      value: 'YOUTUBE_ENGAGEMENTS',
      label: 'Engagements',
    },
    {
      value: 'YOUTUBE_VIEWS',
      label: 'Views',
    },
    {
      value: 'YOUTUBE_LIKES',
      label: 'Likes',
    },
    {
      value: 'YOUTUBE_COMMENTS',
      label: 'Comments',
    },
    {
      value: 'CROSS_CHANNEL_EMV',
      label: 'EMV',
    },
  ],
};

export const creatorInsightPostSortOptionsUpdate = {
  INSTAGRAM: [
    {
      value: 'DATE',
      label: 'Recently Published',
    },
    {
      value: 'CROSS_CHANNEL_EMV',
      label: 'EMV',
    },
    {
      value: 'CROSS_CHANNEL_IMPRESSIONS',
      label: 'Views',
    },
  ],
  INSTAGRAM_FEED: [
    {
      value: 'DATE',
      label: 'Recently Published',
    },
    {
      value: 'INSTAGRAM_EFFECTIVENESS',
      label: 'Effectiveness',
    },
    {
      value: 'INSTAGRAM_ENGAGEMENT_RATE',
      label: 'Engagement Rate',
    },
    {
      value: 'INSTAGRAM_TOTAL_ENGAGEMENTS',
      label: 'Total Engagement',
    },
    {
      value: 'INSTAGRAM_REACH',
      label: 'Reach',
    },
    {
      value: 'INSTAGRAM_EMV',
      label: 'EMV',
    },
    {
      value: 'INSTAGRAM_FOLLOWERS_GAINED',
      label: 'Followers Gained',
    },
    {
      value: 'INSTAGRAM_VIEWS',
      label: 'Views',
    },
  ],
  INSTAGRAM_STORY: [
    {
      value: 'DATE',
      label: 'Recently Published',
    },
    {
      value: 'INSTAGRAM_STORY_FRAME_COMPLETION_RATE_VIEWS',
      label: 'Completion Rate',
    },
    {
      value: 'EMV',
      label: 'EMV',
    },
    {
      value: 'INSTAGRAM_STORY_FRAME_EXIT_RATE_VIEWS',
      label: 'Exit Rate',
    },
    {
      value: 'INSTAGRAM_STORY_FRAME_VIEWS_V2',
      label: 'Views',
    },
  ],
};

export const CREATOR_AVATAR_TOOLTIP = 'Creator deliverables date range:';

export const ADD_CREATORS_WIZARD_STEP_TWO_TEXTS = Object.freeze({
  NO_CREATORS: {
    PANEL_TITLE: 'Select Date Range',
    MESSAGE_HEADER: 'Select your creator Campaign dates',
    MESSAGE_BODY:
      'Choose a date range to automatically import content from your selected creators. Only posts mentioning your brand and published within this period will be added to this Campaign.',
  },
  HAS_CREATORS: {
    PANEL_TITLE: 'Review Date Range',
    MESSAGE_HEADER: 'Dates applied to Campaign. Add Creators to proceed.',
    MESSAGE_BODY:
      'Only posts mentioning your brand and published within this period will be added to this Campaign. Changes to this date range will affect all creator posts in this Campaign.',
  },
});

export const ADD_CREATORS_WIZARD_CAMPAIGN_DETAILS_TEXTS = Object.freeze({
  PANEL_TITLE: 'Campaign Details',
  MESSAGE_HEADER: 'Set up your creator campaign',
  MESSAGE_BODY:
    'Add the details creators will see in your gifting link, then choose whether to include a gifting collection.',
});

export const CAMPAIGN_DETAILS_FORM_TEXTS = Object.freeze({
  INTRO:
    'These details are shared with creators through your gifting link. Use clear names and descriptions so they can easily find and select the right campaign and collection.',
  GIFTING_TITLE: 'Creator Gifting Collection',
  NAME_TOOLTIP:
    "Enter the campaign name you want creators to see. Your internal campaign name won't be affected.",
  DATE_RANGE_TOOLTIP:
    'Choose a date range to automatically import content from your selected creators. Only posts mentioning your brand and published within this period will be added to this Campaign.',
  BUDGET_TOOLTIP:
    'Total budget for all collections within this campaign. This is visible to the creator you share with.',
  DUE_DATE_TOOLTIP:
    'The date you want the creator to submit their order. This is visible to the creator you share with.',
  SUPPORT_EMAIL_TOOLTIP:
    'Creators will see this email if they have questions about their gift or order.',
});

export const EDIT_CAMPAIGN_DETAILS_MENU_LABEL = 'Edit Creator Campaign Details';

export const CAMPAIGN_DETAILS_DATE_RANGE_PRESETS = Object.freeze([
  { value: 'ALL_TIME', label: 'All Time' },
]);

export const topPerformingCreatorsSortOptions = [
  { label: 'EMV', value: 'TOTAL_EMV' },
  { label: 'Likes', value: 'LIKES' },
  { label: 'Comments', value: 'COMMENTS' },
  { label: 'Avg. Engagements', value: 'AVG_ENGAGEMENT' },
  { label: 'Avg. Engagement Rate', value: 'AVG_ENGAGEMENT_RATE' },
  { label: 'Avg. Reach', value: 'AVG_REACH' },
  { label: 'Video Views', value: 'VIDEO_VIEWS' },
  { label: 'Impressions', value: 'TOTAL_IMPRESSIONS' },
  { label: 'Avg. Impressions', value: 'AVG_IMPRESSIONS' },
  { label: 'Avg. Completion Rate', value: 'AVG_COMPLETION_RATE' },
  { label: 'Instagram Followers', value: 'INSTAGRAM_FOLLOWERS' },
  { label: 'TikTok Followers', value: 'TIKTOK_FOLLOWERS' },
  { label: 'YouTube Subscribers', value: 'YOUTUBE_SUBSCRIBERS' },
];

const POST_PERFORMANCE_POST_TYPES = {
  TIKTOK: 'TIKTOK',
  INSTAGRAM_FEED: 'FEED_POSTS',
  INSTAGRAM_STORY: 'STORY',
  YOUTUBE: 'YOUTUBE',
};

export const POST_PERFORMANCE_DEFAULT_POST_TYPES = [];

// Gated behind the `creators-ig-collabs` flag - sc-210623
export const IG_COLLAB_MEDIA_TYPES = ['INSTAGRAM_COLLAB', 'INSTAGRAM_OWNED'];

export const postTypeToMediaType = {
  TIKTOK_POSTS: ['TIKTOK_UGC'],
  INSTAGRAM_FEED_REELS: ['INSTAGRAM_UGC', 'INSTAGRAM_COLLAB', 'INSTAGRAM_OWNED'],
  INSTAGRAM_STORIES: ['INSTAGRAM_STORY_UGC'],
  YOUTUBE_VIDEOS: ['YOUTUBE_UGC'],
  YOUTUBE_SHORTS: ['YOUTUBE_UGC'],
};

export const POST_PERFORMANCE_POST_TYPES_SELECTED = {
  TIKTOK_POST: [POST_PERFORMANCE_POST_TYPES.TIKTOK],
  INSTAGRAM_FEED: [POST_PERFORMANCE_POST_TYPES.INSTAGRAM_FEED],
  INSTAGRAM_STORY: [POST_PERFORMANCE_POST_TYPES.INSTAGRAM_STORY],
  INSTAGRAM_ALL_POST_TYPES: [
    POST_PERFORMANCE_POST_TYPES.INSTAGRAM_FEED,
    POST_PERFORMANCE_POST_TYPES.INSTAGRAM_STORY,
  ],
  YOUTUBE: [POST_PERFORMANCE_POST_TYPES.YOUTUBE],
};

export const POST_PERFORMANCE_DEFAULT_SORT = ['-DATE'];

export const POST_PERFORMANCE_DEFAULT_MEDIA_TYPES = [
  'INSTAGRAM_UGC',
  'INSTAGRAM_STORY_UGC',
  'INSTAGRAM_COLLAB',
  'INSTAGRAM_OWNED',
  'TIKTOK_UGC',
  'YOUTUBE_UGC',
];

export const REPORTING_PERIOD_SPECIAL_PRESETS = Object.freeze({
  ALL_TIME: {
    value: 'ALL_TIME',
    label: 'All Time',
  },
  CUSTOM: {
    value: 'CUSTOM',
    label: 'Custom',
  },
});

// This matches dashing-ui's `PresetType` type.
export const REPORTING_PERIOD_PRESETS = Object.freeze({
  LAST_24_HOURS: {
    value: 'LAST_24_HOURS',
    label: 'Last 24 Hours',
    range: ({ dateService } = {}) => lastDateRange(1, 'day', dateService),
  },
  LAST_3_DAYS: {
    value: 'LAST_3_DAYS',
    label: 'Last 3 Days',
    range: ({ dateService } = {}) => lastDateRange(3, 'day', dateService),
  },
  LAST_7_DAYS: {
    value: 'LAST_7_DAYS',
    label: 'Last 7 Days',
    range: ({ dateService } = {}) => lastDateRange(7, 'day', dateService),
  },
  LAST_4_WEEKS: {
    value: 'LAST_4_WEEKS',
    label: 'Last 4 Weeks',
    range: ({ dateService } = {}) => lastDateRange(28, 'day', dateService),
  },
});

export const CAMPAIGN_DETAIL_FILTER_SCOPE = 'campaignDetail';

export const CAMPAIGN_SWITCH_SCALE_THRESHOLD_DAYS = 60;

export const SOCIAL_COMMERCE_TOOLTIPS = Object.freeze({
  USERS: 'The number of unique users who have logged at least one event',
  SESSIONS: 'The number of website or app sessions initiated',
  CONVERSION_RATE: 'The percentage of sessions triggering a specific purchase conversion event',
  TRANSACTIONS: 'The number of transaction events with purchase revenue',
  REVENUE:
    'The sum of revenue during the reporting period from purchases, subscriptions and advertising, minus refunded transaction revenue',
});

export const SOCIAL_COMMERCE_TABLE_SORT_OPTIONS = Object.freeze([
  { label: 'Source / Medium', value: 'sourceMedium' },
  { label: 'Users', value: 'totalUsers' },
  { label: 'Sessions', value: 'sessions' },
  { label: 'Conversion Rate', value: 'ecommerceConversionRate' },
  { label: 'Transactions', value: 'transactions' },
  { label: 'Revenue', value: 'revenue' },
]);

export const CAMPAIGN_RELATIONSHIP_METRICS = Object.freeze([
  {
    title: 'Posts',
    valueKey: 'number_of_media',
    tooltip: 'The number of posts that mentioned or tagged you',
    format: 'number',
    postType: 'ORGANIC',
    metricTimeFrame: 'POSTS_THIS_PERIOD',
  },
  {
    title: 'Followers Gained',
    valueKey: 'followers_gained',
    tooltip: 'The estimated number of followers gained for you',
    format: 'number',
    postType: 'ORGANIC',
    metricTimeFrame: 'POSTS_THIS_PERIOD',
  },
  {
    title: 'Avg. Engagement Rate',
    valueKey: 'avg_engagement_rate',
    tooltip:
      "The average percentage of the poster's audience that engaged\n\nEngagement Rate = (Likes + Comments) / Followers",
    format: 'percent',
    formatOptions: {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    postType: 'ORGANIC',
    metricTimeFrame: 'POSTS_THIS_PERIOD',
  },
  {
    title: 'Avg. Effectiveness',
    valueKey: 'avg_effectiveness_rate',
    tooltip:
      'The percentage of unique accounts who saw these posts and engaged with it\n\nEffectiveness = (Likes + Comments) / Est. Reach',
    format: 'percent',
    formatOptions: {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    postType: 'ORGANIC',
    metricTimeFrame: 'POSTS_THIS_PERIOD',
  },
  {
    title: 'Engagements',
    valueKey: 'engagements',
    tooltip:
      'The total number of engagements received\n\nEngagements = Likes + Comments + (Saves + Shares + Reposts if connected)',
    format: 'number',
    postType: 'ORGANIC',
    metricTimeFrame: 'POSTS_THIS_PERIOD',
  },
  {
    title: 'Total EMV',
    valueKey: 'total_emv',
    tooltip:
      "The Earned Media Value of posts in this Campaign.\n\nThis value is based on the creators' audience size, media type, and number of engagements.",
    format: 'currency',
    formatOptions: { maximumFractionDigits: 0 },
    postType: 'ORGANIC',
    metricTimeFrame: 'POSTS_THIS_PERIOD',
  },
]);

export const NO_DATA_FOUND = 'No posts found during this reporting period.';
export const EMPTY_CHART_DATA_MESSAGE = 'Data during the reporting period will be displayed here';
export const ERROR_CHART_DATA_MESSAGE =
  'There was an issue loading your data. Please refresh the page to try again.';
export const PLATFORM_DISCONNECTED_MESSAGE = (platform) =>
  `Your ${platform} account has been disconnected. Please reconnect to see applicable data.`;

export const MEDIA_LIST_EMPTY_STATES = Object.freeze({
  NO_MEDIA_STATE: {
    title: "Couldn't Find a Match",
    variant: 'sectionEmpty',
    icon: 'search',
    message: 'Review or adjust your filters to uncover more results',
    ctaButton: { label: 'Reset Filters' },
  },
  ERROR_STATE: {
    title: "Couldn't Load Posts",
    variant: 'sectionEmpty',
    icon: 'alert-triangle',
    message: 'There was an issue loading your posts. Please refresh the page to try again.',
  },
});

export const ADS_LIST_EMPTY_STATES = Object.freeze({
  NO_ADS: {
    title: 'Your Ads will appear here',
    variant: 'sectionEmpty',
  },
  ERROR: {
    title: "Couldn't Load Ads",
    variant: 'sectionEmpty',
    icon: 'alert-triangle',
    message: 'There was an issue loading your ads. Please refresh the page to try again.',
  },
});

export const CAMPAIGN_EMPTY_STATE_IMAGES = {
  ADD_BOARDS: {
    BANNER_SRC: emptyCampaignAddBoards1x,
    BANNER_SRC_SET: srcSet(
      emptyCampaignAddBoards1x,
      emptyCampaignAddBoards2x,
      emptyCampaignAddBoards3x,
    ),
  },
  ADD_MEDIA: {
    BANNER_SRC: emptyCampaignAddMedia1x,
    BANNER_SRC_SET: srcSet(
      emptyCampaignAddMedia1x,
      emptyCampaignAddMedia2x,
      emptyCampaignAddMedia3x,
    ),
  },
  ADD_CREATORS: {
    BANNER_SRC: emptyCampaignAddCreators1x,
    BANNER_SRC_SET: srcSet(
      emptyCampaignAddCreators1x,
      emptyCampaignAddCreators2x,
      emptyCampaignAddCreators3x,
    ),
  },
  ADD_AD_CAMPAIGNS: {
    BANNER_SRC: emptyCampaignAddAdCampaigns1x,
    BANNER_SRC_SET: srcSet(
      emptyCampaignAddAdCampaigns1x,
      emptyCampaignAddAdCampaigns2x,
      emptyCampaignAddAdCampaigns3x,
    ),
  },
};

export const CAMPAIGN_EMPTY_STATE_IMAGES_NEW = {
  ADD_BOARDS: {
    BANNER_SRC: newEmptyCampaignAddBoards1x,
    BANNER_SRC_SET: srcSet(
      newEmptyCampaignAddBoards1x,
      newEmptyCampaignAddBoards2x,
      newEmptyCampaignAddBoards3x,
    ),
  },
  ADD_MEDIA: {
    BANNER_SRC: newEmptyCampaignAddMedia1x,
    BANNER_SRC_SET: srcSet(
      newEmptyCampaignAddMedia1x,
      newEmptyCampaignAddMedia2x,
      newEmptyCampaignAddMedia3x,
    ),
  },
  ADD_CREATORS: {
    BANNER_SRC: newEmptyCampaignAddCreators1x,
    BANNER_SRC_SET: srcSet(
      newEmptyCampaignAddCreators1x,
      newEmptyCampaignAddCreators2x,
      newEmptyCampaignAddCreators3x,
    ),
  },
  ADD_AD_CAMPAIGNS: {
    BANNER_SRC: newEmptyCampaignAddAdCampaigns1x,
    BANNER_SRC_SET: srcSet(
      newEmptyCampaignAddAdCampaigns1x,
      newEmptyCampaignAddAdCampaigns2x,
      newEmptyCampaignAddAdCampaigns3x,
    ),
  },
};

export const CAMPAIGN_METRICS = {
  creator: [
    {
      label: 'Avg. Engagements',
      value: 'avg_engagements',
      formatType: 'number',
    },
    {
      label: 'Avg. Engagement Rate',
      value: 'avg_engagement_rate',
      formatType: 'percent',
      formatOptions: {
        style: 'percent',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    },
    {
      label: 'Likes',
      value: 'total_creator_likes',
      formatType: 'number',
    },
    {
      label: 'EMV',
      value: 'total_emv',
      formatType: 'currency',
      formatOptions: { maximumFractionDigits: 0 },
    },
  ],
  owned: [
    {
      label: 'Avg. Engagement Rate',
      value: 'avg_engagement_rate',
      formatType: 'percent',
      formatOptions: {
        style: 'percent',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    },
    {
      label: 'Engagements',
      value: 'total_engagements',
      formatType: 'number',
    },
    {
      label: 'Impressions',
      value: 'total_impressions',
      formatType: 'number',
    },
    {
      label: 'Video Views',
      value: 'total_video_views',
      formatType: 'number',
    },
  ],
};

export const SHOPIFY_PRODUCT_FILTER_DETAILS = {
  scope: 'shopifyProducts',
  defaultState: {
    price: { min: null, max: null },
    inventoryCount: { min: null, max: null },
    stockStatus: [],
    category: [],
    type: [],
  },
  options: {
    stockStatus: [
      { label: 'In Stock', value: 'IN_STOCK' },
      { label: 'Out of Stock', value: 'OUT_OF_STOCK' },
    ],
  },
};

export const METRIC_TYPE_TO_DASHING_UI_FORMAT = Object.freeze({
  [METRIC_DATA_TYPES.INTEGER]: Formatters.FORMAT_TYPES.NUMBER,
  [METRIC_DATA_TYPES.FLOAT]: Formatters.FORMAT_TYPES.NUMBER,
  [METRIC_DATA_TYPES.MONETARY]: Formatters.FORMAT_TYPES.CURRENCY,
  [METRIC_DATA_TYPES.PERCENTAGE]: Formatters.FORMAT_TYPES.PERCENT,
  [METRIC_DATA_TYPES.SECONDS]: Formatters.FORMAT_TYPES.DURATION,
  [METRIC_DATA_TYPES.MILLISECONDS]: Formatters.FORMAT_TYPES.DURATION,
});

export const FRACTIONAL_METRIC_TYPES = Object.freeze([
  METRIC_DATA_TYPES.FLOAT,
  METRIC_DATA_TYPES.PERCENTAGE,
]);

export const CAMPAIGN_ADS_GRAPHS = Object.freeze({
  [AD_SOURCES.META_ADS]: [
    {
      title: 'Amount Spent vs ROAS',
      tooltip: {
        content:
          'Track your Meta ad spend against return on ad spend (ROAS). Use this to evaluate paid campaign efficiency.',
        footer: 'ROAS = Purchase Conversion ÷ Amount Spent',
      },
      metrics: [{ key: 'TOTAL_SPENT' }, { key: 'RETURN_ON_AD_SPEND' }],
    },
    {
      title: 'Clicks (All) vs CPC',
      tooltip:
        'Track total ad clicks alongside cost per click to help optimize campaigns for cost-effective engagement',
      metrics: [{ key: 'CLICKS' }, { key: 'COST_PER_CLICK' }],
    },
  ],
  [AD_SOURCES.TIKTOK_ADS]: [
    {
      title: 'Amount Spent vs Reach',
      tooltip:
        'Compares your TikTok ad spend to the number of users reached over time. Use this to understand how efficiently your budget is scaling audience exposure.',
      metrics: [{ key: 'TOTAL_SPENT' }, { key: 'REACH' }],
    },
    {
      title: 'Clicks vs CPC',
      tooltip:
        'Track total ad clicks alongside cost per click to help optimize campaigns for cost-effective conversion',
      metrics: [{ key: 'CLICKS' }, { key: 'COST_PER_CLICK' }],
    },
  ],
});

export const CAMPAIGN_ADS_DEFAULT_SORT = {
  [AD_SOURCES.META_ADS]: '-CTR',
  [AD_SOURCES.META_ADS_CAMPAIGNS_DETAIL]: 'CTR',
  [AD_SOURCES.TIKTOK_ADS]: '-TIKTOK_AD_CPM',
};

export const CAMPAIGN_ADS_SORT_OPTIONS = {
  [AD_SOURCES.META_ADS]: [
    { value: 'SPEND', metricKey: 'TOTAL_SPENT' },
    { value: 'CLICKS' },
    { value: 'CONVERSION_RATE' },
    { value: 'CPC', metricKey: 'COST_PER_CLICK' },
    { value: 'CPM', metricKey: 'COST_PER_MILE' },
    { value: 'CTR', metricKey: 'CLICK_THROUGH_RATE' },
    { value: 'ENGAGEMENT_RATE' },
    { value: 'ENGAGEMENTS' },
    { value: 'FREQUENCY' },
    { value: 'IMPRESSIONS' },
    { value: 'LINK_CLICKS' },
    { value: 'PAGE_ENGAGEMENTS' },
    { value: 'REACH' },
    { value: 'ROAS', metricKey: 'RETURN_ON_AD_SPEND' },
    { value: 'VIDEO_VIEWS' },
    { value: 'WEB_CONVERSIONS' },
  ],
  [AD_SOURCES.TIKTOK_ADS]: [
    { value: 'TIKTOK_AD_AMOUNT_SPENT', metricKey: 'TOTAL_SPENT' },
    { value: 'TIKTOK_AD_CLICKS', metricKey: 'CLICKS' },
    { value: 'TIKTOK_AD_CLICKS_ALL', metricKey: 'ENGAGEMENTS' },
    { value: 'TIKTOK_AD_CPC', metricKey: 'COST_PER_CLICK' },
    { value: 'TIKTOK_AD_CPM', metricKey: 'COST_PER_MILE' },
    { value: 'TIKTOK_AD_CTR', metricKey: 'CLICK_THROUGH_RATE_DESTINATION' },
    { value: 'TIKTOK_AD_ENGAGED_VIEWS', metricKey: 'ENGAGED_VIEW' },
    { value: 'TIKTOK_AD_FREQUENCY', metricKey: 'FREQUENCY' },
    { value: 'TIKTOK_AD_IMPRESSIONS', metricKey: 'IMPRESSIONS' },
    { value: 'TIKTOK_AD_REACH', metricKey: 'REACH' },
    { value: 'TIKTOK_AD_REAL_TIME_CONVERSION_RATE', metricKey: 'REAL_TIME_CONVERSION_RATE' },
    { value: 'TIKTOK_AD_REAL_TIME_CONVERSION', metricKey: 'REAL_TIME_CONVERSIONS' },
    { value: 'TIKTOK_AD_VIDEO_VIEWS_50_PERCENT', metricKey: 'VIDEO_VIEWS_P50' },
    { value: 'TIKTOK_AD_VIDEO_VIEWS_100_PERCENT', metricKey: 'VIDEO_VIEWS_P100' },
    { value: 'TIKTOK_AD_VIDEO_WATCHED_6_SEC', metricKey: 'VIDEO_WATCHED_6S' },
  ],
};

export const EXCLUDED_POSTS_MESSAGE = {
  title: 'Excluded Posts',
  message:
    'These posts are now excluded from your Campaign performance metrics. To see all excluded posts, use the filter options below.',
  severity: 'info',
  ctaText: "Don't Show Again",
};

export const INCLUDED_POSTS_MESSAGE = {
  title: 'Included Posts',
  message:
    'These posts are now included in your Campaign performance metrics. To see all included posts, use the filter options below.',
  severity: 'info',
  ctaText: "Don't Show Again",
};

export const CREATOR_INSIGHTS_ERROR_MESSAGES = {
  INCLUDE_POSTS_ERROR:
    'An error occurred while including posts. Please wait a moment before trying again',
  EXCLUDE_POSTS_ERROR:
    'An error occurred while excluding posts. Please wait a moment before trying again',
};

export const AD_CAMPAIGN_STATUS_CONFIG = Object.freeze({
  ACTIVE: {
    label: 'Active',
    badgeSeverity: 'success',
  },
  PAUSED: {
    label: 'Paused',
    badgeSeverity: 'warning',
  },
  ARCHIVED: {
    label: 'Archived',
    badgeSeverity: 'statusNeutral',
  },
  DISABLE: {
    label: 'Paused',
    badgeSeverity: 'warning',
  },
  ENABLE: {
    label: 'Active',
    badgeSeverity: 'success',
  },
  DELETE: {
    label: 'Deleted',
    badgeSeverity: 'statusNeutral',
  },
  BUDGET_EXCEED: {
    label: 'Overbudget',
    badgeSeverity: 'error',
  },
  WITH_ISSUES: {
    label: 'With Issues',
    badgeSeverity: 'error',
  },
});

export const CREATOR_CAMPAIGNS_CHANNEL_OPTIONS = [
  {
    label: 'TikTok',
    value: 'TIKTOK',
    icon: 'tiktok-color',
    mediaTypes: ['TIKTOK_UGC'],
  },
  {
    label: 'Instagram',
    value: 'INSTAGRAM',
    icon: 'instagram-color',
    mediaTypes: ['INSTAGRAM_UGC', 'INSTAGRAM_STORY_UGC', 'INSTAGRAM_COLLAB', 'INSTAGRAM_OWNED'],
  },
  {
    label: 'YouTube',
    value: 'YOUTUBE',
    icon: 'youtube-color',
    mediaTypes: ['YOUTUBE_UGC'],
  },
];

export const CREATOR_CAMPAIGNS_POST_TYPE_OPTIONS = [
  {
    label: 'TikTok Posts',
    value: 'TIKTOK_POSTS',
    icon: 'tiktok-color',
    requiredChannels: ['TIKTOK'],
  },
  {
    label: 'Instagram Feed and Reels',
    value: 'INSTAGRAM_FEED_REELS',
    icon: 'instagram-color',
    requiredChannels: ['INSTAGRAM'],
  },
  {
    label: 'Instagram Stories',
    value: 'INSTAGRAM_STORIES',
    icon: 'instagram-story-color',
    requiredChannels: ['INSTAGRAM'],
  },
  {
    label: 'YouTube Videos',
    value: 'YOUTUBE_VIDEOS',
    icon: 'youtube-color',
    requiredChannels: ['YOUTUBE'],
    tooltip: 'YouTube post types can only be refined when YouTube channel is selected alone',
  },
  {
    label: 'YouTube Shorts',
    value: 'YOUTUBE_SHORTS',
    icon: 'youtube-short-color',
    requiredChannels: ['YOUTUBE'],
    tooltip: 'YouTube post types can only be refined when YouTube channel is selected alone',
  },
];

export const campaignCreatorInsightsYouTubeChannelStats = [
  {
    label: 'Creators',
    value: 'totalCreators',
    formatType: 'formatIntegerAbbreviated',
    tooltip: 'The number of creators in this campaign with a YouTube handle added to their profile',
  },
  {
    label: 'Subscribers',
    value: 'audienceSize',
    formatType: 'formatIntegerAbbreviated',
    tooltip: 'The number of subscribers this creator has on YouTube',
  },
  {
    label: 'EMV',
    value: 'emv',
    formatType: 'formatCurrency',
    tooltip:
      'The total earned media value for all YouTube posts in this campaign.' +
      '\n\n' +
      'EMV is calculated by factoring in the channel, audience size, content type, engagement and current market values for comparable posts.',
  },
  {
    label: 'Avg. EMV',
    value: 'avgEmv',
    formatType: 'formatCurrency',
    tooltip:
      'The average earned media value of YouTube creators in this campaign.\n\nEMV is calculated by factoring in the channel, content type, engagement and current market values for comparable posts.',
  },
  {
    label: 'Engagements',
    value: 'engagements',
    formatType: 'formatIntegerAbbreviated',
    tooltip:
      'The total engagements across all YouTube creators’ posts in this campaign.\n\nEngagements = Likes + Comments',
  },
  {
    label: 'Avg. Engagements',
    value: 'avgEngagements',
    formatType: 'formatIntegerAbbreviated',
    tooltip:
      'The average number of engagements per YouTube post in this campaign.\n\nEngagements = Likes + Comments',
  },
  {
    label: 'Avg. Engagement Rate',
    value: 'avgEngagementRate',
    formatType: 'formatPercentage',
    tooltip:
      'The average engagement rate across YouTube posts in this campaign.\n\nEngagement Rate = (Likes + Comments) / Views',
  },
  {
    label: 'Views',
    value: 'views',
    formatType: 'formatIntegerAbbreviated',
    tooltip: 'The total number of video views across all YouTube posts in this campaign',
  },
  {
    label: 'Avg. Views',
    value: 'avgViews',
    formatType: 'formatIntegerAbbreviated',
    tooltip: 'The average number of video views per YouTube post across this campaign',
  },
  {
    label: 'Likes',
    value: 'likes',
    formatType: 'formatIntegerAbbreviated',
    tooltip: 'The total number of likes across all YouTube posts in this campaign',
  },
  {
    label: 'Avg. Likes',
    value: 'avgLikes',
    formatType: 'formatIntegerAbbreviated',
    tooltip: 'The average number of likes per YouTube post across this campaign',
  },
  {
    label: 'Comments',
    value: 'comments',
    formatType: 'formatIntegerAbbreviated',
    tooltip: 'The total number of comments across all YouTube posts in this campaign',
  },
  {
    label: 'Avg. Comments',
    value: 'avgComments',
    formatType: 'formatIntegerAbbreviated',
    tooltip: 'The average number of comments per YouTube post across this campaign',
  },
  {
    label: 'Net New Subscribers',
    value: 'netNewSubscribers',
    formatType: 'formatIntegerAbbreviated',
    tooltip:
      'The net number of new YouTube subscribers your channel has gained or lost during this campaign date range.',
  },
];

export const HIDDEN_YOUTUBE_METRICS = [
  'avgLikes',
  'avgComments',
  'emv',
  'avgEmv',
  'engagements',
  'avgEngagements',
  'avgEngagementRate',
  'netNewSubscribers',
];

export const CAMPAIGN_BENCHMARKS_GRAPH_COLORS = Object.freeze({
  CAMPAIGN_AVERAGE_MARKER: '#19A695',
  COMPARISON_CAMPAIGN_MARKER: '#F75C9C',
  CHANNEL_AVERAGE_MARKER: '#0872ee',
  CAMPAIGN_DATA: '#3F6B9F',
});

export const COMPARISON_CAMPAIGNS_LEGEND_ITEM_VALUE = 'ComparisonCampaignsLegendItem';

export const AFFILIATE_LINKS = Object.freeze({
  TOOLTIPS: {
    LINK_NAME: 'A name to identify your affiliate link',
    DOMAIN: 'The domain used for this link',
    CUSTOM_BACK_HALF: 'The custom back half used for this link',
    UTM_PARAMETERS:
      'Toggle on to add campaign tracking details (source, medium, term, content, campaign). Toggle off to hide these fields.',
    UTM_SOURCE: 'Identifies where the traffic is coming from (e.g., facebook, tiktok, newsletter)',
    UTM_MEDIUM: 'Defines the type of traffic channel (e.g., cpc, email, social)',
    UTM_CAMPAIGN:
      'Pre-filled with the Dash campaign name and creator handle. You can edit this to customize tracking.',
    UTM_TERM: 'Keyword for paid search ads',
    UTM_CONTENT: 'Optional label to differentiate ad creatives',
    TARGET_URL: 'The original destination this affiliate link redirects to',
    SHORT_LINK: 'The affiliate link that redirects to the target URL',
    CLICKS: 'The number of times this affiliate link has been clicked',
  },
  BANNER_INFO: {
    TITLE: 'Domain selection is final',
    MESSAGE:
      "Once you choose a domain and save your affiliate link, the domain can't be changed. To use a different domain, you'll need to create a new link.",
  },
  BANNER_ERROR: {
    TITLE: 'Bitly Error',
    MESSAGE:
      "We couldn't complete your request with Bitly. This may be due to usage limits or other restrictions. Please try again, or contact Bitly Support if the issue continues.",
  },
  DELETE_CONFIRMATION: {
    HEADER: 'Delete Link?',
    MESSAGE:
      "Deleting this link will remove its details and click data from Dash Social. This action can't be undone.\n\nTo delete the link permanently, remove it from your Bitly account.",
    ACCEPT_LABEL: 'Delete',
  },
  TRACKING_EVENTS: {
    DELETE_POPUP_OPENED: 'Delete link popup opened',
    LINK_DELETED: 'Link Deleted',
  },
});

export const TOPLINE_METRICS_BULK_APPLY_WARNING =
  "Only metrics will be applied to the campaigns. Goals won't copy over - existing goals are kept only for metrics in this set, and lost for any that aren't included.";

export const TOPLINE_METRICS_SELECTED_APPLY_WARNING =
  'Metrics and goals will apply to the current campaign. Other selected campaigns will get the metric updates but goals will not copy over. Existing goals are kept for metrics in this set and removed for any that are not.';

export const TOPLINE_METRICS_CURRENT_CAMPAIGN_TOOLTIP =
  'The campaign you are viewing always receives these changes';

export const TOPLINE_METRICS_APPLY_DISABLED_TOOLTIPS = Object.freeze({
  NO_CHANGES: "You haven't made any changes",
  NO_CAMPAIGNS_SELECTED: 'Select a campaign to apply these metrics to',
  CURRENT_CAMPAIGN_ONLY: 'Select another campaign or change a metric to apply',
});

export const TOPLINE_METRICS_SAVE_OPTIONS = Object.freeze({
  CURRENT_CAMPAIGN: 'Apply to This Campaign Only',
  ALL_CAMPAIGNS: 'Apply to All Campaigns',
  SELECTED_CAMPAIGNS: 'Apply to Selected Campaigns',
});

export const TOPLINE_METRICS_SAVE_ERRORS = Object.freeze({
  DEFAULT: 'Could not save your metrics. Please try again.',
  BULK_APPLY:
    'The metric selections were applied to this campaign but were not applied to the other campaigns. Please try again.',
});

export const TOPLINE_METRICS_SAVE_DIALOGS = Object.freeze({
  CURRENT_CAMPAIGN: {
    header: 'Apply Metric Settings',
    message: 'The metric and goal settings will only apply to the campaign',
    acceptLabel: 'Apply',
  },
  ALL_CAMPAIGNS: {
    header: 'Apply to All Campaigns',
    message: TOPLINE_METRICS_BULK_APPLY_WARNING,
    acceptLabel: 'Apply',
  },
});

export const CAMPAIGN_BENCHMARKS_PAGE = Object.freeze({
  OWNED_SUMMARY: 'OWNED_SUMMARY',
  SOCIAL_ADS_SUMMARY: 'SOCIAL_ADS_SUMMARY',
  CREATOR_SUMMARY: 'CREATOR_SUMMARY',
});

export const DEFAULT_CAMPAIGN_TOPLINE_SECTION_TITLE = 'Campaign Metrics';

export const CAMPAIGN_TOPLINE_SECTION_TITLES = Object.freeze({
  [CAMPAIGN_PAGE.CREATOR_SUMMARY]: 'Campaign Performance',
  [CAMPAIGN_PAGE.SOCIAL_ADS_SUMMARY]: 'Ad Campaigns Metrics',
});

// Mirrors LEGACY_OWNED_METRIC_TO_DASHBOARDS_METRIC in library-backend.
export const OWNED_LEGACY_TO_DASHBOARDS_METRIC = Object.freeze({
  number_of_media: 'NUMBER_OF_POSTS',
  video_views_v2: 'TOTAL_VIDEO_VIEWS_V2',
  engagements: 'TOTAL_ENGAGEMENTS_V2',
  avg_engagement_rate: 'AVG_ENGAGEMENT_RATE_IMPRESSIONS',
  impressions_v2: 'IMPRESSIONS_V3',
  link_clicks: 'TOTAL_CLICKS',
});

export const OWNED_DASHBOARDS_TO_LEGACY_METRIC = Object.freeze(
  Object.fromEntries(
    Object.entries(OWNED_LEGACY_TO_DASHBOARDS_METRIC).map(([legacy, dashboards]) => [
      dashboards,
      legacy,
    ]),
  ),
);

export const OWNED_SUMMARY_DEFAULT_METRICS = Object.freeze([
  {
    source: METRIC_SOURCE.CAMPAIGN_STATS,
    metric: 'number_of_media',
  },
  {
    source: METRIC_SOURCE.CAMPAIGN_STATS,
    metric: 'video_views_v2',
  },
  {
    source: METRIC_SOURCE.CAMPAIGN_STATS,
    metric: 'engagements',
  },
  {
    source: METRIC_SOURCE.CAMPAIGN_STATS,
    metric: 'avg_engagement_rate',
  },
  {
    source: METRIC_SOURCE.CAMPAIGN_STATS,
    metric: 'impressions_v2',
  },
  {
    source: METRIC_SOURCE.CAMPAIGN_STATS,
    metric: 'link_clicks',
  },
]);

export const SENTIMENT_COLORS = Object.freeze({
  positive: TOKENS.COLOR_SENTIMENT_POSITIVE,
  negative: TOKENS.COLOR_SENTIMENT_NEGATIVE,
  neutral: TOKENS.COLOR_SENTIMENT_NEUTRAL,
});

export const ACTIVE_PRODUCT_ORDER_STATUSES = Object.freeze([
  'IN_PROGRESS',
  'ON_HOLD',
  'PARTIALLY_FULFILLED',
  'SCHEDULED',
  'UNFULFILLED',
  'PENDING_FULFILLMENT',
]);

// Matches the column the name is stored in, which every campaign endpoint validates against.
export const CAMPAIGN_NAME_MAX_LENGTH = 64;

// A name only gets a character counter once it is this close to the cap.
export const CAMPAIGN_NAME_COUNTER_THRESHOLD = CAMPAIGN_NAME_MAX_LENGTH - 20;

export const DUPLICATE_CAMPAIGN_CONTENT_OPTIONS = Object.freeze([
  { value: DUPLICATE_CAMPAIGN_CONTENT.POSTS, label: 'Posts', icon: 'media-type' },
  {
    value: DUPLICATE_CAMPAIGN_CONTENT.CREATORS,
    label: 'Creators',
    icon: 'user-avatar',
    tooltipIcon:
      'Only the creators you select will be duplicated; deliverables, affiliate links, and product orders will not be copied',
  },
  { value: DUPLICATE_CAMPAIGN_CONTENT.ADS, label: 'Ads', icon: 'ad' },
]);

export const DUPLICATE_CAMPAIGN_CONTENT_TOOLTIP =
  'Content can only be duplicated within the same brand';

export const DUPLICATE_CAMPAIGN_METRICS_TOOLTIP =
  'All metrics from this campaign will be copied to the new one';

export const VALID_CAMPAIGN_BRAND_MEDIA_TYPES = Object.freeze([
  enumTypes.FACEBOOK_OWNED,
  enumTypes.FACEBOOK_LINK,
  enumTypes.FACEBOOK_TEXT_LINK,
  enumTypes.FACEBOOK_TEXT,
  enumTypes.INSTAGRAM_OWNED,
  enumTypes.INSTAGRAM_OWNED_IGTV,
  enumTypes.INSTAGRAM_COLLAB,
  enumTypes.INSTAGRAM_STORY,
  enumTypes.PINTEREST_OWNED,
  enumTypes.TIKTOK_OWNED,
  enumTypes.TWITTER_OWNED,
  enumTypes.TWITTER_OWNED_LINK,
  enumTypes.TWITTER_OWNED_TEXT,
  enumTypes.YOUTUBE_OWNED,
  enumTypes.INSTAGRAM_UGC,
  enumTypes.INSTAGRAM_UGC_IGTV,
  enumTypes.INSTAGRAM_STORY_UGC,
  enumTypes.TIKTOK_UGC,
]);

export const GIFTING_PRODUCT_SEARCH_DEBOUNCE_MS = 300;

export const GIFTING_SELECT_PRODUCTS_FILTERS = Object.freeze({
  scope: 'giftingSelectProducts',
  default: { categoryId: null },
});

export const SELECT_PRODUCTS_PANEL_TEXTS = Object.freeze({
  EXACT_MATCH_GUIDANCE: 'Use "quotes" around your search term for an exact match',
  NO_RESULTS_STATE: {
    title: 'No results found',
    message:
      'You may want to try different keywords, checking for typos, or adjusting your filters',
    cta: 'Clear Search',
  },
  ERROR_STATE: {
    title: 'API Error',
    message: 'Something went wrong. Please try again.',
    cta: 'Retry',
  },
  ADDED_TOOLTIP: 'This variant has already been added. Edit colors and sizes in the next step.',
});

export const SELECT_VARIANTS_PANEL_TEXTS = Object.freeze({
  EMPTY_STATE: {
    title: 'No products selected',
    message: 'Go back and pick products to include in this collection.',
  },
  NO_RESULTS_STATE: {
    title: 'No results found',
    message: 'No selected products match that SKU. You may want to check for typos.',
    cta: 'Clear Search',
  },
  FAILED_STATE: {
    title: "Some products couldn't be added",
    message: "We couldn't load them from the catalog. Try adding them again.",
  },
  UNAVAILABLE_STATE: {
    title: 'Some products are no longer available',
    message: "They've left the catalog, so they won't be saved with this collection.",
  },
  UNAVAILABLE_OPTION: 'This variant is not available',
});

export const GIFTING_DRAWERS = Object.freeze({
  CREATE_COLLECTION: 'createGiftingCollection',
});

export const CREATE_GIFTING_COLLECTION_TEXTS = Object.freeze({
  TITLE: 'Create Collection',
  LOAD_ERROR: "Couldn't load this campaign's gifting settings.",
  RETRY: 'Try Again',
  CAMPAIGN_DETAILS_TITLE: 'Campaign Details',
  NEW_COLLECTION_TITLE: 'New Collection',
  SELECT_PRODUCTS_TITLE: 'Select Products',
  SELECT_VARIANTS_TITLE: 'Select Variants',
  REVIEW_TITLE: 'Review Collection',
  SAVE_AND_NEXT: 'Save and Next',
  FINISH: 'Save',
  NAME_LABEL: 'Name',
  NAME_PLACEHOLDER: 'e.g. Fresh Summer of the Year',
  THUMBNAIL_LABEL: 'Collection Thumbnail',
  INCOMPLETE_TOOLTIP: 'Every product needs at least one color and size',
  DETAILS_ERROR: 'Could not save the campaign details. Please try again.',
  RESOLVING_TOOLTIP: 'Still loading the products you added',
  COLLECTION_ERROR: 'Could not save the collection. Please try again.',
  PRODUCTS_ERROR: 'Could not save the products. Please try again.',
  SUCCESS: 'Collection created',
});

// Only the copy that differs when editing; the rest of the wizard reads from the create texts.
export const EDIT_GIFTING_COLLECTION_TEXTS = Object.freeze({
  TITLE: 'Edit Collection',
  LOAD_ERROR: "Couldn't load this collection.",
  NEW_COLLECTION_TITLE: 'Edit Collection',
  SUCCESS: 'Collection updated',
});

export const REVIEW_COLLECTION_PANEL_TEXTS = Object.freeze({
  DUE_DATE_LABEL: 'Due Date',
  BUDGET_LABEL: 'Budget',
  REMOVE_PRODUCT: 'Remove Product',
  EMPTY_STATE: {
    title: 'No products selected',
    message: 'Go back and pick products to include in this collection.',
  },
});
